import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import css from './StudentDashboard.module.css';
import teacherModalCss from './TeacherDashboard.module.css';
import SchoolExamStarted from './SchoolExamStarted';
import { renderMarkdownNote } from '../../utils/client-functions';
import CareerPathSummaryCard from './CareerPathSummaryCard';
import ReportCardLibrary from './ReportCardLibrary';
import { compressImageIfNeeded, validateImageFile, convertToJpeg } from '../../utils/imageUtils';
import SchoolPasswordInput from './SchoolPasswordInput';

// Grading UI — reuse InsightNTutor styled components
import {
  GlassCard, StatGrid, StatCard,
  ScoreCircleWrapper, ScoreText,
  ProgressBarTrack, ProgressBarFill, Badge,
} from '../../styled/InsightNTutor';
import {
  FaTrophy, FaChartLine, FaHourglass, FaUsers,
  FaChartPie, FaRobot, FaCheckCircle, FaTimesCircle,
  FaChevronDown, FaChevronUp, FaRegBookmark,
  FaVideo, FaBookOpen, FaBookmark, FaClipboardList,
} from 'react-icons/fa';

// MUI icons
import DashboardIcon from '@mui/icons-material/Dashboard';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BarChartIcon from '@mui/icons-material/BarChart';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import LockIcon from '@mui/icons-material/Lock';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import InsightsIcon from '@mui/icons-material/Insights';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ScheduleIcon from '@mui/icons-material/Schedule';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ForumIcon from '@mui/icons-material/Forum';
import MessagingSegment from './MessagingSegment';
import TimetableGrid from './TimetableGrid';
import SchoolPWAInstallBanner from './SchoolPWAInstallBanner';
import SchoolPoweredFooter from './SchoolPoweredFooter';
import SchoolNotificationCenter from './SchoolNotificationCenter';

import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';

// ─── Constants ────────────────────────────────────────────────────────────────

const NAV = [
  { key: 'overview',      label: 'Overview',                Icon: DashboardIcon     },
  { key: 'exams',         label: 'Exams & Tests',           Icon: AssignmentIcon    },
  { key: 'results',       label: 'Results & Performance',   Icon: BarChartIcon      },
  { key: 'curriculum',    label: 'Curriculum',              Icon: MenuBookIcon      },
  { key: 'calendar',      label: 'Calendar',                Icon: CalendarMonthIcon },
  { key: 'timetable',     label: 'Timetable',               Icon: ScheduleIcon     },
  { key: 'attendance',    label: 'Attendance',              Icon: EventAvailableIcon },
  { key: 'messages',      label: 'Messages',                Icon: ForumIcon },
  { key: 'career-path',   label: 'Your Career Path',        Icon: AutoAwesomeIcon   },
  { key: 'notifications', label: 'Notifications & Feedback',Icon: NotificationsIcon },
  { key: 'profile',       label: 'Profile & Settings',      Icon: PersonIcon        },
];

// ─── Shared helpers ───────────────────────────────────────────────────────────

function initials(firstName = '', lastName = '') {
  return `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase() || '?';
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtDateShort(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

function fmtDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function fmtShortDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function fmtTermLabel(term) {
  if (!term) return '—';
  return term === 'first' ? 'First' : term === 'second' ? 'Second' : term === 'third' ? 'Third' : term;
}

function statusLabel(s) {
  const map = {
    upcoming: 'Upcoming', active: 'Active', completed: 'Completed',
    missed: 'Missed', in_progress: 'In Progress', retake_available: 'Retake available',
  };
  return map[s] || s;
}

function statusChipClass(s) {
  return {
    upcoming: css.chipBlue, active: css.chipTeal, completed: css.chipGreen,
    retake_available: css.chipBlue,
    missed: css.chipRed, in_progress: css.chipYellow,
  }[s] || css.chipGray;
}

// A retake window overrides the (completed) exam status wherever the exam is Active or Upcoming.
function effectiveStatus(exam) {
  const availability = exam.retake?.availability;
  if (availability === 'upcoming') return 'upcoming';
  if (availability === 'available' || availability === 'in_progress') return 'active';
  return exam.status;
}

function SkeletonRow({ cols = 5 }) {
  return (
    <tr>
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i}><div className={css.skel} style={{ height: 14, width: '80%', borderRadius: 6 }} /></td>
      ))}
    </tr>
  );
}

function SkeletonCards({ n = 4 }) {
  return Array.from({ length: n }).map((_, i) => (
    <div key={i} className={css.statCard}>
      <div className={css.skel} style={{ height: 12, width: '60%', marginBottom: 10 }} />
      <div className={css.skel} style={{ height: 28, width: '40%' }} />
    </div>
  ));
}

// ─── Fetch helper ─────────────────────────────────────────────────────────────

function studentFetch(path, opts = {}) {
  const token   = localStorage.getItem('school-token');
  const schoolId = localStorage.getItem('school-id');
  const isFormData = typeof FormData !== 'undefined' && opts.body instanceof FormData;
  const headers = {
    'x-access-token': token || '',
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(opts.headers || {}),
  };
  if (schoolId) headers['x-school-id'] = schoolId;
  return fetch(path, { ...opts, headers }).then(async response => {
    if (response.status === 401) {
      const data = await response.clone().json().catch(() => ({}));
      if (data.code === 'PASSWORD_CHANGED') {
        sessionStorage.setItem('student-password-changed-notice', data.error || 'Your password was changed. Sign in with your new password.');
        localStorage.removeItem('school-token');
        localStorage.removeItem('school-actor');
        localStorage.removeItem('school-id');
        localStorage.removeItem('token');
        localStorage.removeItem('school-student-must-change-pwd');
        window.location.assign('/school');
      }
    }
    return response;
  });
}

// ─── SEGMENT: Overview ────────────────────────────────────────────────────────

function OverviewSegment({ profile, onGoToExams, onInsight }) {
  const [progress, setProgress] = useState(null);
  const [exams, setExams]       = useState([]);
  const [overviewExams, setOverviewExams] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [activeExamState, setActiveExamState] = useState(null);
  const [starting, setStarting] = useState(false);

  const load = useCallback(() => {
    Promise.all([
      studentFetch('/student/progress').then(r => r.json()),
      studentFetch('/student/exams?limit=100').then(r => r.json()),
    ]).then(([prog, ex]) => {
      setProgress(prog.progress || null);
      setOverviewExams(ex.exams || []);
      setExams((ex.exams || []).slice(0, 3));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const refreshTimer = setInterval(load, 60 * 1000);
    return () => clearInterval(refreshTimer);
  }, [load]);

  // Active / Upcoming exam banner
  const activeExam   = overviewExams.find(e => effectiveStatus(e) === 'active');
  const upcomingExam = overviewExams.find(e => effectiveStatus(e) === 'upcoming');

  const handleStartExam = async (exam) => {
    if (starting) return;
    setStarting(true);
    try {
      const res = await studentFetch(`/student/exams/${exam.schoolExamId}/start`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) { alert(data.error || 'Cannot start exam'); return; }
      setActiveExamState({
        exam: { title: data.title, instructions: data.instructions, settings: data.settings },
        questions: data.questions,
        subjectSections: data.subjectSections || [],
        attempt: data.attempt,
        serverNow: data.serverNow,
        schoolExamId: exam.schoolExamId,
      });
    } catch (err) {
      alert('Cannot start exam. Please try again.');
    } finally {
      setStarting(false);
    }
  };

  const handleGradingComplete = useCallback((result) => {
    setActiveExamState(null);
    load();
    if (result?.schoolExamId && onInsight) {
      onInsight(result.schoolExamId, 'grading');
    }
  }, [load, onInsight]);

  if (activeExamState) {
    return (
      <SchoolExamStarted
        exam={activeExamState.exam}
        questions={activeExamState.questions}
        subjectSections={activeExamState.subjectSections}
        attempt={activeExamState.attempt}
        serverNow={activeExamState.serverNow}
        schoolExamId={activeExamState.schoolExamId}
        onClose={() => setActiveExamState(null)}
        onGradingComplete={handleGradingComplete}
      />
    );
  }

  return (
    <div>
      {/* Greeting banner */}
      <div className={css.banner}>
        <div>
          <p className={css.bannerTitle}>
            Welcome back, {profile?.firstName || 'Student'}!
          </p>
          <p className={css.bannerSub}>
            {activeExam
              ? activeExam.retake?.status === 'pending'
                ? `Retake available: ${activeExam.title}`
                : activeExam.retake?.status === 'in_progress'
                  ? `Retake in progress: ${activeExam.title}`
                  : `Exam in progress: ${activeExam.title}`
              : upcomingExam
              ? `${upcomingExam.retake?.availability === 'upcoming' ? 'Upcoming retake' : 'Upcoming exam'}: ${upcomingExam.title}`
              : 'No upcoming exams. Keep revising!'}
          </p>
          {activeExam && (
            <button
              onClick={() => handleStartExam(activeExam)}
              disabled={starting}
              style={{
                marginTop: 8, background: 'rgba(255,255,255,0.22)', color: '#fff',
                border: '1.5px solid rgba(255,255,255,0.5)', borderRadius: 8,
                padding: '7px 18px', cursor: starting ? 'not-allowed' : 'pointer',
                fontWeight: 700, fontSize: '0.85rem', opacity: starting ? 0.7 : 1,
                display: 'inline-flex', alignItems: 'center', gap: 8,
              }}
            >
              {starting
                ? <><span className={css.btnSpinner} /> Starting…</>
                : <>▶ {activeExam.retake?.status === 'pending' ? 'Start Retake' : activeExam.retake?.status === 'in_progress' ? 'Resume Retake' : activeExam.attemptStatus === 'in_progress' ? 'Continue Exam' : 'Start Exam'}</>}
            </button>
          )}
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          {/* Active term badge — comes from school.currentTerm via profile */}
          {profile?.term && (
            <div style={{ marginBottom: 6 }}>
              <span style={{
                background: 'rgba(255,255,255,0.18)', color: '#fff',
                fontSize: '0.73rem', fontWeight: 700, borderRadius: 20,
                padding: '3px 10px', letterSpacing: '0.04em', textTransform: 'uppercase',
              }}>
                {profile.term === 'first' ? 'First' : profile.term === 'second' ? 'Second' : 'Third'} Term
                {profile.session ? ` · ${profile.session}` : ''}
              </span>
            </div>
          )}
          {upcomingExam && !activeExam && (
            <>
              <span className={`${css.chip} ${statusChipClass(effectiveStatus(upcomingExam))}`}>
                {statusLabel(effectiveStatus(upcomingExam))}
              </span>
              <p style={{ margin: '4px 0 0', fontSize: '0.78rem', color: 'rgba(255,255,255,.75)' }}>
                {fmtDate(upcomingExam.retake?.startDate || upcomingExam.settings?.startDate)}
              </p>
            </>
          )}
        </div>
      </div>

      {/* Stat cards */}
      <div className={css.statsRow}>
        {loading ? <SkeletonCards n={5} /> : (
          <>
            <div className={css.statCard}>
              <span className={css.statLabel}>Exams Assigned</span>
              <span className={css.statValue}>{progress?.totalAssigned ?? exams.length}</span>
              <span className={css.statSub}>in your school</span>
            </div>
            <div className={css.statCard}>
              <span className={css.statLabel}>CA Assigned</span>
              <span className={css.statValue}>{progress?.caAssigned ?? '—'}</span>
              <span className={css.statSub}>continuous assessments</span>
            </div>
            <div className={css.statCard}>
              <span className={css.statLabel}>Completed</span>
              <span className={css.statValue}>{progress?.examsAttempted ?? '—'}</span>
              <span className={css.statSub}>exams taken</span>
            </div>
            <div className={css.statCard}>
              <span className={css.statLabel}>Average Score</span>
              <span className={css.statValue} style={{ color: '#0c7c84' }}>
                {progress?.avgPercentage != null ? `${progress.avgPercentage}%` : '—'}
              </span>
              <span className={css.statSub}>across all exams</span>
            </div>
            <div className={css.statCard}>
              <span className={css.statLabel}>Pass Rate</span>
              <span className={css.statValue} style={{ color: '#10b981' }}>
                {progress?.examsAttempted
                  ? `${Math.round((progress.examsPassed / progress.examsAttempted) * 100)}%`
                  : '—'}
              </span>
              <span className={css.statSub}>{progress?.examsPassed ?? 0} passed</span>
            </div>
          </>
        )}
      </div>

      {/* Weak topics nudge */}
      {progress?.weakTopics?.length > 0 && (
        <div className={css.card}>
          <h3 style={{ margin: '0 0 14px', fontSize: '0.92rem', color: '#0c1e28' }}>
            Areas to Review
          </h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {progress.weakTopics.slice(0, 8).map(t => (
              <span key={t.topic} className={`${css.chip} ${css.chipRed}`}>{t.topic}</span>
            ))}
          </div>
        </div>
      )}

      {/* Recent exams strip */}
      {exams.length > 0 && (
        <div className={css.card}>
          <h3 style={{ margin: '0 0 16px', fontSize: '0.92rem', color: '#0c1e28' }}>Recent Exams</h3>
          <div className={css.tableWrap}>
          <table className={css.table}>
            <thead>
              <tr>
                <th>Exam</th>
                <th>Subject</th>
                <th>Status</th>
                <th>Score</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {exams.slice(0, 3).map(e => (
                <tr
                  key={String(e.schoolExamId)}
                  style={{ cursor: e.status === 'completed' && onInsight ? 'pointer' : undefined }}
                  onClick={() => { if (e.status === 'completed' && onInsight) onInsight(e.schoolExamId, 'grading'); }}
                >
                  <td style={{ fontWeight: 600 }}>
                    {e.title}
                    {e.retakeCount > 0 && <span className={css.chip} style={{ marginLeft: 8, background: '#fff7ed', color: '#9a3412', border: '1px solid #fed7aa' }}>Retaken ×{e.retakeCount}</span>}
                  </td>
                  <td>{e.isMultiSubject ? <span className={css.chip} style={{ background: 'rgba(95,190,197,0.14)', color: '#0f3135', border: '1px solid rgba(95,190,197,0.4)' }}>Multi-Subject</span> : (e.subjectName || '—')}</td>
                  <td><span className={`${css.chip} ${statusChipClass(e.status)}`}>{statusLabel(e.status)}</span></td>
                  <td>
                    {e.resultVisibility === 'immediate' && e.percentage != null
                      ? `${e.percentage}%`
                      : e.status === 'completed'
                        ? <span style={{ fontSize: '0.78rem', color: '#94a3b8', fontStyle: 'italic' }}>Pending</span>
                        : '—'}
                  </td>
                  <td>{fmtDateTime(e.submittedAt || e.settings?.startDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── SEGMENT: Exams & Tests ───────────────────────────────────────────────────

function ExamsSegment({ onInsight }) {
  const [tab, setTab]           = useState('active');
  const [exams, setExams]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [activeExam, setActiveExam] = useState(null);  // { exam, questions, schoolExamId }
  const [startingId, setStartingId] = useState(null);

  const load = useCallback((showLoading = true) => {
    if (showLoading) setLoading(true);
    studentFetch('/student/exams?limit=100').then(r => r.json()).then(data => {
      setExams(data.exams || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    const refreshTimer = setInterval(() => load(false), 60 * 1000);
    return () => clearInterval(refreshTimer);
  }, [load]);

  const filtered = exams.filter(e => {
    if (tab === 'upcoming')  return effectiveStatus(e) === 'upcoming';
    if (tab === 'active')    return effectiveStatus(e) === 'active' || e.retake?.status === 'grading';
    if (tab === 'completed') return e.status === 'completed';
    if (tab === 'missed')    return e.status === 'missed';
    return true;
  });

  const handleStartExam = async (exam) => {
    if (startingId) return;
    setStartingId(exam.schoolExamId);
    try {
      const res = await studentFetch(`/student/exams/${exam.schoolExamId}/start`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) { alert(data.error || 'Cannot start exam'); return; }
      setActiveExam({
        exam: { title: data.title, instructions: data.instructions, settings: data.settings },
        questions: data.questions,
        subjectSections: data.subjectSections || [],
        attempt: data.attempt,
        serverNow: data.serverNow,
        schoolExamId: exam.schoolExamId,
      });
    } catch (err) {
      alert('Cannot start exam. Please try again.');
    } finally {
      setStartingId(null);
    }
  };

  const handleGradingComplete = useCallback((result) => {
    setActiveExam(null);
    load();
    if (result?.schoolExamId && onInsight) {
      // Switch to Results & Performance tab and open this exam's grading
      onInsight(result.schoolExamId, 'grading');
    }
  }, [load, onInsight]);

  if (activeExam) {
    return (
      <SchoolExamStarted
        exam={activeExam.exam}
        questions={activeExam.questions}
        subjectSections={activeExam.subjectSections}
        attempt={activeExam.attempt}
        serverNow={activeExam.serverNow}
        schoolExamId={activeExam.schoolExamId}
        onClose={() => setActiveExam(null)}
        onGradingComplete={handleGradingComplete}
      />
    );
  }

  return (
    <div>
      <div className={css.sectionHeader}>
        <h2 className={css.sectionTitle}>Exams & Tests</h2>
      </div>

      <div className={css.tabs}>
        {[
          { k: 'active',    l: 'Active'    },
          { k: 'upcoming',  l: 'Upcoming'  },
          { k: 'completed', l: 'Completed' },
          { k: 'missed',    l: 'Missed'    },
        ].map(({ k, l }) => (
          <button
            key={k}
            className={`${css.tab} ${tab === k ? css.tabActive : ''}`}
            onClick={() => setTab(k)}
          >
            {l}
          </button>
        ))}
      </div>

      <div className={css.card} style={{ padding: 0, overflow: 'hidden' }}>
        <div className={css.tableWrap}>
        <table className={css.table}>
          <thead>
            <tr>
              <th>Exam Title</th>
              <th>Subject</th>
              <th>Duration</th>
              <th>Date &amp; Time</th>
              <th>Term</th>
              <th>Session</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading
              ? Array.from({ length: 4 }).map((_, i) => <SkeletonRow key={i} cols={8} />)
              : filtered.length === 0
                ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', padding: '40px 12px', color: '#90a4ae' }}>
                      No {tab} exams
                    </td>
                  </tr>
                )
                : filtered.map(e => {
                  const showRetake = e.retake?.status === 'pending';
                  const showResumeRetake = e.retake?.status === 'in_progress';
                  const showGradingRetake = e.retake?.status === 'grading';
                  const hasRetakeWindow = !!e.retake && tab !== 'completed';
                  const rowStatus = tab === 'completed' ? e.status : effectiveStatus(e);
                  const showStart  = showRetake
                    ? e.retake?.availability === 'available'
                    : showResumeRetake || (e.status === 'active' && !showGradingRetake);
                  const showInsights = e.status === 'completed' && e.examId;
                  const retakeStart = e.retake?.startDate;
                  const retakeEnd = e.retake?.endDate;
                  const retakeStatusLabel = e.retake?.availability === 'upcoming'
                    ? `Opens ${fmtShortDate(retakeStart)}`
                    : `Until ${fmtShortDate(retakeEnd)}`;
                  return (
                    <tr key={String(e.schoolExamId)}>
                      <td style={{ fontWeight: 600 }}>
                        {e.title}
                        {e.retakeCount > 0 && <span className={css.chip} style={{ marginLeft: 8, background: '#fff7ed', color: '#9a3412', border: '1px solid #fed7aa' }}>Retaken ×{e.retakeCount}</span>}
                      </td>
                      <td>{e.isMultiSubject ? <span className={css.chip} style={{ background: 'rgba(95,190,197,0.14)', color: '#0f3135', border: '1px solid rgba(95,190,197,0.4)' }}>Multi-Subject</span> : (e.subjectName || '—')}</td>
                      <td>{e.settings?.duration ? `${e.settings.duration} min` : '—'}</td>
                      <td>{fmtDateTime(hasRetakeWindow ? retakeStart : e.settings?.startDate)}</td>
                      <td>{fmtTermLabel(e.term)}</td>
                      <td>{e.session || '—'}</td>
                      <td>
                        <div className={css.examStatusPills}>
                          <span className={`${css.chip} ${css.examStatusPill} ${statusChipClass(rowStatus)}`}>{statusLabel(rowStatus)}</span>
                          {showRetake && (
                            <span className={`${css.examStatusPill} ${css.retakeStatusPill}`} title={`Retake window: ${fmtDateTime(retakeStart)} to ${fmtDateTime(retakeEnd)}`}>
                              {retakeStatusLabel}
                            </span>
                          )}
                          {showResumeRetake && <span className={`${css.examStatusPill} ${css.inProgressRetakePill}`}>Retake in progress</span>}
                          {showGradingRetake && <span className={`${css.examStatusPill} ${css.gradingRetakePill}`}>Grading retake</span>}
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {showStart && (
                            <button className={css.primaryBtn} disabled={startingId === e.schoolExamId} onClick={() => handleStartExam(e)}>
                              {startingId === e.schoolExamId
                                ? <><span className={css.btnSpinner} /> Starting…</>
                                : <><PlayArrowIcon style={{ fontSize: '1rem' }} /> {showRetake ? 'Start Retake' : showResumeRetake ? 'Resume Retake' : e.attemptStatus === 'in_progress' ? 'Continue Exam' : 'Start'}</>}
                            </button>
                          )}
                          {showGradingRetake && <button className={css.outlineBtn} disabled>Grading retake…</button>}
                          {showInsights && (
                            <button
                              className={css.outlineBtn}
                              title="View grading, study plan, and AI tutor for this exam"
                              onClick={() => onInsight(e.schoolExamId)}
                            >
                              <InsightsIcon style={{ fontSize: '1rem' }} /> Insights
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

// ─── SEGMENT: Results & Performance ──────────────────────────────────────────

function ResultsSegment({ jumpTo, onJumpCleared }) {
  const [progress, setProgress]       = useState(null);
  const [exams, setExams]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [selected, setSelected]       = useState(null);
  const [defaultTab, setDefaultTab]   = useState('grading');
  const [studyPlan, setStudyPlan]     = useState(null);
  const [studyLoading, setStudyLoading] = useState(false);
  const [studyJobId, setStudyJobId]   = useState(null);
  const [studyError, setStudyError]   = useState(null);
  // Term filter — '' = all terms
  const [filterTerm, setFilterTerm]   = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  // Practice mock from study plan
  const [practiceModal, setPracticeModal] = useState({
    open: false, loading: false, questions: null,
    title: '', topic: '', mockMins: 0, schoolExamId: null, error: null, taskIdx: null,
  });
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    Promise.all([
      studentFetch('/student/progress').then(r => r.json()),
      studentFetch('/student/exams?limit=100').then(r => r.json()),
    ]).then(([prog, ex]) => {
      if (!alive) return;
      setProgress(prog.progress || null);
      const completed = (ex.exams || []).filter(e => e.status === 'completed');
      setExams(completed);
      setLoading(false);
    }).catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  // Auto-open when navigated from ExamsSegment Insights button
  useEffect(() => {
    if (!jumpTo || loading || exams.length === 0) return;
    const target = exams.find(e => String(e.schoolExamId) === String(jumpTo.id));
    if (target) {
      setSelected(target);
      setDefaultTab(jumpTo.tab || 'grading');
      if (onJumpCleared) onJumpCleared();
    }
  }, [jumpTo, loading, exams]); // eslint-disable-line

  // Performance over time chart data — only include exams with released scores
  const chartData = exams
    .filter(e => e.percentage != null && e.resultVisibility === 'immediate')
    .sort((a, b) => new Date(a.submittedAt) - new Date(b.submittedAt))
    .map(e => ({ name: fmtDateShort(e.submittedAt), score: e.percentage }));

  // Term/session options derived from available exam data
  const termOptions = Array.from(
    new Map(exams.filter(e => e.term).map(e => [`${e.term}||${e.session}`, { term: e.term, session: e.session }]))
    .values()
  ).sort((a, b) => {
    const order = { first: 0, second: 1, third: 2 };
    return (order[a.term] ?? 9) - (order[b.term] ?? 9);
  });

  // Subject options derived from available exam data
  const subjectOptions = Array.from(
    new Set(exams.map(e => e.subjectName).filter(Boolean))
  ).sort();

  // Apply term + subject filters
  const filteredExams = exams.filter(e => {
    if (filterTerm) {
      // Support both combined "term||session" keys (from termOptions) and plain term values
      const combined = `${e.term}||${e.session}`;
      if (combined !== filterTerm && e.term !== filterTerm) return false;
    }
    if (filterSubject && e.subjectName !== filterSubject) return false;
    return true;
  });

  const termLabel = (t) => t === 'first' ? 'First' : t === 'second' ? 'Second' : t === 'third' ? 'Third' : t;

  // Start a practice mock — generates fresh AI questions on the task topic
  const handleStartMock = useCallback(async (examObj, overrideDurationMins, topic, taskIdx) => {
    const cleanTopic = topic || examObj?.title || 'General Practice';
    setPracticeModal({ open: true, loading: true, questions: null, title: examObj?.title || '', topic: cleanTopic, mockMins: overrideDurationMins, schoolExamId: examObj?.schoolExamId, error: null, taskIdx: taskIdx ?? null });
    try {
      const res = await studentFetch(`/student/exams/${examObj.schoolExamId}/generate-mock-questions`, {
        method: 'POST',
        body: JSON.stringify({ topic: cleanTopic, mockDurationMins: overrideDurationMins }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to generate questions');
      setPracticeModal(prev => ({ ...prev, loading: false, questions: data.questions }));
    } catch (err) {
      setPracticeModal(prev => ({ ...prev, loading: false, error: err.message || 'Failed to generate mock questions' }));
    }
  }, []);

  // Load study plan for selected exam
  useEffect(() => {
    if (!selected?.examId) return;
    // Only generate study plan when results have been officially released
    if (selected?.resultVisibility !== 'immediate') return;
    setStudyPlan(null); setStudyError(null); setStudyJobId(null);

    studentFetch('/student/study-plan/generate', {
      method: 'POST',
      body: JSON.stringify({ examObjectId: String(selected.examId) }),
    }).then(r => r.json()).then(data => {
      if (data.status === 'perfect') {
        setStudyPlan({ _perfect: true, message: data.message });
      } else if (data.status === 'done' && data.studyPlan) {
        setStudyPlan(data.studyPlan);
      } else if (data.jobId) {
        setStudyJobId(data.jobId);
      } else {
        setStudyError(data.error || 'Could not generate study plan');
      }
    }).catch(() => setStudyError('Network error'));
  }, [selected?.examId, selected?.resultVisibility]);

  // SSE polling for study plan generation
  useEffect(() => {
    if (!studyJobId) return;
    let cancelled = false;
    setStudyLoading(true);

    async function poll() {
      try {
        const res = await studentFetch(`/student/study-plan/generation-progress/${studyJobId}`);
        if (!res.ok || !res.body) { setStudyError('Connection error'); return; }
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';
        while (true) {
          if (cancelled) break;
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const lines = buf.split('\n');
          buf = lines.pop();
          for (const line of lines) {
            if (!line.startsWith('data:')) continue;
            try {
              const ev = JSON.parse(line.slice(5).trim());
              if (ev.status === 'completed' && ev.studyPlan) {
                setStudyPlan(ev.studyPlan);
                setStudyLoading(false);
                return;
              }
              if (ev.status === 'failed') { setStudyError('Failed to generate study plan'); setStudyLoading(false); return; }
            } catch {}
          }
        }
      } catch { setStudyError('Network error during study plan generation'); setStudyLoading(false); }
    }
    poll();
    return () => { cancelled = true; };
  }, [studyJobId]);

  const toggleTask = async (taskIdx, currentDone) => {
    if (!selected?.examId) return;
    await studentFetch('/student/study-plan/update-progress', {
      method: 'POST',
      body: JSON.stringify({ examObjectId: String(selected.examId), taskIdx, done: !currentDone }),
    });
    // Optimistically update
    setStudyPlan(prev => {
      if (!prev?.schedule) return prev;
      const allTasks = prev.schedule.flatMap(d => d.tasks);
      if (allTasks[taskIdx]) allTasks[taskIdx].done = !currentDone;
      return { ...prev };
    });
  };

  // Force-marks a single task done (used for auto-completion triggers — e.g.
  // opening a video/AI-notes CTA, or finishing the linked practice mock — as
  // opposed to toggleTask's manual checkbox flip). Idempotent no-op if the
  // task is already done, so it never accidentally un-ticks anything.
  const markTaskDone = async (taskIdx) => {
    if (!selected?.examId || taskIdx == null) return;
    const allTasksNow = (studyPlan?.schedule || []).flatMap(d => d.tasks || []);
    if (allTasksNow[taskIdx]?.done) return;
    await studentFetch('/student/study-plan/update-progress', {
      method: 'POST',
      body: JSON.stringify({ examObjectId: String(selected.examId), taskIdx, done: true }),
    });
    setStudyPlan(prev => {
      if (!prev?.schedule) return prev;
      const allTasks = prev.schedule.flatMap(d => d.tasks);
      if (allTasks[taskIdx]) allTasks[taskIdx].done = true;
      return { ...prev };
    });
  };

  return (
    <>
      {practiceModal.open && (
        <PracticeExamModal
          title={practiceModal.title}
          topic={practiceModal.topic}
          mockMins={practiceModal.mockMins}
          questions={practiceModal.questions}
          loading={practiceModal.loading}
          error={practiceModal.error}
          schoolExamId={practiceModal.schoolExamId}
          onClose={() => setPracticeModal(prev => ({ ...prev, open: false }))}
          onComplete={() => markTaskDone(practiceModal.taskIdx)}
        />
      )}
      <div>
        <div className={css.sectionHeader}>        <h2 className={css.sectionTitle}>Results & Performance</h2>
      </div>

      {/* Stat cards */}
      <div className={css.statsRow}>
        {loading ? <SkeletonCards n={4} /> : (
          <>
            <div className={css.statCard}>
              <span className={css.statLabel}>Exams Completed</span>
              <span className={css.statValue}>{progress?.examsAttempted ?? 0}</span>
            </div>
            <div className={css.statCard}>
              <span className={css.statLabel}>Passed</span>
              <span className={css.statValue} style={{ color: '#10b981' }}>{progress?.examsPassed ?? 0}</span>
            </div>
            <div className={css.statCard}>
              <span className={css.statLabel}>Avg Score</span>
              <span className={css.statValue} style={{ color: '#0c7c84' }}>
                {progress?.avgPercentage != null ? `${progress.avgPercentage}%` : '—'}
              </span>
            </div>
            <div className={css.statCard}>
              <span className={css.statLabel}>Pass Rate</span>
              <span className={css.statValue}>
                {progress?.examsAttempted
                  ? `${Math.round((progress.examsPassed / progress.examsAttempted) * 100)}%`
                  : '—'}
              </span>
            </div>
          </>
        )}
      </div>

      {/* Performance chart */}
      {chartData.length > 1 && (
        <div className={css.card}>
          <h3 style={{ margin: '0 0 18px', fontSize: '0.92rem', color: '#0c1e28' }}>
            Score Over Time
          </h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f4f8" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
              <Tooltip formatter={v => [`${v}%`, 'Score']} />
              <Line type="monotone" dataKey="score" stroke="#5fbec5" strokeWidth={2} dot={{ r: 4, fill: '#5fbec5' }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Subject breakdown */}
      {(progress?.subjectBreakdown || []).length > 0 && (
        <div className={css.card}>
          <h3 style={{ margin: '0 0 16px', fontSize: '0.92rem', color: '#0c1e28' }}>
            Subject Breakdown
          </h3>
          {progress.subjectBreakdown.map(s => (
            <div key={s.subject} style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.83rem', marginBottom: 5 }}>
                <span style={{ fontWeight: 600, color: '#0c1e28' }}>{s.subject}</span>
                <span style={{ color: '#90a4ae' }}>{s.avgPercentage}%</span>
              </div>
              <div className={css.progressTrack}>
                <div className={css.progressFill} style={{ width: `${s.avgPercentage}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Weak topics */}
      {(progress?.weakTopics || []).length > 0 && (
        <div className={css.card}>
          <h3 style={{ margin: '0 0 12px', fontSize: '0.92rem', color: '#0c1e28' }}>Weak Topics</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {progress.weakTopics.map(t => (
              <span key={t.topic} className={`${css.chip} ${css.chipRed}`}>{t.topic} ×{t.count}</span>
            ))}
          </div>
        </div>
      )}

      <ReportCardLibrary
        fetchFn={studentFetch}
        listPath="/student/report-cards"
        downloadPath={card => `/student/report-card?term=${card.term}&session=${encodeURIComponent(card.session)}`}
        filenamePrefix="student-report-card"
      />

      <ReportCardLibrary
        fetchFn={studentFetch}
        listPath="/student/mid-term-reports"
        downloadPath={card => `/student/mid-term-report?term=${card.term}&session=${encodeURIComponent(card.session)}`}
        filenamePrefix="student-mid-term-report"
        title="Released Mid-Term Results"
        subtitle="Interim results published by the school during the term."
        emptyTitle="No released mid-term results yet"
        emptyDescription="Published mid-term results will appear here automatically."
        titleId="released-mid-term-reports-title"
      />

      {/* Exam history — clickable rows open inline insight panel */}
      <div className={css.card}>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: '0.92rem', color: '#0c1e28', flex: 1 }}>Exam History</h3>

          {/* Term filter — always visible; populated from real exam data when available */}
          <select
            value={filterTerm}
            onChange={e => { setFilterTerm(e.target.value); setSelected(null); }}
            style={{ padding: '5px 10px', border: '1px solid #d0dce0', borderRadius: 8, fontSize: '0.8rem' }}
          >
            <option value="">All Terms</option>
            {termOptions.length > 0
              ? termOptions.map(o => (
                  <option key={`${o.term}||${o.session}`} value={`${o.term}||${o.session}`}>
                    {termLabel(o.term)} Term — {o.session}
                  </option>
                ))
              : [['first','First'],['second','Second'],['third','Third']].map(([val, lbl]) => (
                  <option key={val} value={val}>{lbl} Term</option>
                ))
            }
          </select>

          {/* Subject filter */}
          {subjectOptions.length > 1 && (
            <select
              value={filterSubject}
              onChange={e => { setFilterSubject(e.target.value); setSelected(null); }}
              style={{ padding: '5px 10px', border: '1px solid #d0dce0', borderRadius: 8, fontSize: '0.8rem' }}
            >
              <option value="">All Subjects</option>
              {subjectOptions.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          )}

        </div>

        {loading ? (
          <div className={css.tableWrap}><table className={css.table}><tbody>{Array.from({length:3}).map((_,i)=><SkeletonRow key={i} cols={7}/>)}</tbody></table></div>
        ) : filteredExams.length === 0 ? (
          <div className={css.emptyState}>
            <BarChartIcon style={{ fontSize: '2rem', color: '#c0ccda' }} />
            <p className={css.emptyTitle}>No completed exams yet</p>
            <p className={css.emptyDesc}>Results will appear here after you submit an exam.</p>
          </div>
        ) : (
          <div className={css.tableWrap}>
          <table className={css.table}>
            <thead>
              <tr>
                <th>Exam</th><th>Subject</th><th>Score</th><th>Result</th><th>Date &amp; Time</th><th>Term</th><th>Session</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredExams.map(e => {
                const isOpen = selected?.schoolExamId === e.schoolExamId;
                return (
                <React.Fragment key={String(e.schoolExamId)}>
                  <tr
                    style={{ cursor: 'pointer', background: isOpen ? '#f0f9fa' : undefined, transition: 'background 0.15s' }}
                    onClick={() => { setSelected(isOpen ? null : e); setDefaultTab('grading'); }}
                  >
                    <td style={{ fontWeight: 600 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: '0.7rem', color: isOpen ? '#5fbec5' : '#b0bec5', transition: 'transform 0.2s', display: 'inline-block', transform: isOpen ? 'rotate(90deg)' : 'none' }}>▶</span>
                        {e.title}
                        {e.retakeCount > 0 && <span className={css.chip} style={{ marginLeft: 8, background: '#fff7ed', color: '#9a3412', border: '1px solid #fed7aa' }}>Retaken ×{e.retakeCount}</span>}
                      </span>
                    </td>
                    <td>{e.isMultiSubject ? <span className={css.chip} style={{ background: 'rgba(95,190,197,0.14)', color: '#0f3135', border: '1px solid rgba(95,190,197,0.4)' }}>Multi-Subject</span> : (e.subjectName || '—')}</td>
                    <td>{e.resultVisibility === 'immediate' && e.percentage != null ? `${e.percentage}%` : '—'}</td>
                    <td>
                      {e.resultVisibility === 'immediate'
                        ? <span className={`${css.chip} ${e.passed ? css.chipGreen : css.chipRed}`}>{e.passed ? 'Passed' : 'Failed'}</span>
                        : <span className={css.chip} style={{ background: '#f1f5f8', color: '#78909c', border: '1px solid #cfd8dc' }}>Pending</span>}
                    </td>
                    <td>{fmtDateTime(e.submittedAt)}</td>
                    <td>{fmtTermLabel(e.term)}</td>
                    <td>{e.session || '—'}</td>
                    <td onClick={ev => ev.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                        {[['grading', 'Grading', '📊'], ['studyplan', 'Study Plan', '📚'], ['tutor', 'AI Tutor', '🤖']].map(([tab, label, emoji]) => {
                          const notReleased = e.resultVisibility !== 'immediate' && (tab === 'studyplan' || tab === 'tutor');
                          return (
                            <button
                              key={tab}
                              className={isOpen && defaultTab === tab ? css.primaryBtn : css.outlineBtn}
                              style={{ fontSize: '0.73rem', padding: '4px 9px', opacity: notReleased ? 0.45 : 1, cursor: notReleased ? 'not-allowed' : 'pointer' }}
                              title={notReleased ? 'Available once results are released' : label}
                              disabled={notReleased}
                              onClick={() => { if (!notReleased) { setSelected(e); setDefaultTab(tab); } }}
                            >
                              {emoji} {label}
                            </button>
                          );
                        })}
                      </div>
                    </td>
                  </tr>

                  {/* Inline insight panel */}
                  {isOpen && (
                    <tr>
                      <td colSpan={8} style={{ padding: 0, background: '#f8fafc' }}>
                        <InlineInsightPanel
                          exam={e}
                          defaultTab={defaultTab}
                          studyPlan={studyPlan}
                          studyLoading={studyLoading}
                          studyError={studyError}
                          onToggleTask={toggleTask}
                          onStartMock={handleStartMock}
                          navigate={navigate}
                          resultsReleased={e.resultVisibility === 'immediate'}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  </>
  );
}

// ─── Inline Insight Panel (Grading + Study Plan + AI Tutor) ──────────────────

// ─── GradingDetails ───────────────────────────────────────────────────────────
// Mirrors InsightNTutor's GradingSegment for school exam results.
function GradingDetails({ grading, exam, isMultiSubject, subjectBreakdown = [], activeSubjectIdx = 'all', setActiveSubjectIdx = () => {} }) {
  const isMulti = isMultiSubject && subjectBreakdown.length > 1;
  const currentSubjectData = isMulti && activeSubjectIdx !== 'all'
    ? (subjectBreakdown.find(s => s.subjectIdx === activeSubjectIdx) || null)
    : null;

  // Effective grading view — either the combined exam grading ("All Subjects")
  // or the selected subject's own breakdown. Every stat/section below reads
  // from `eg` so switching the pill re-renders every grading item for that subject.
  const eg = currentSubjectData ? {
    score:            currentSubjectData.score,
    pass:             currentSubjectData.passed,
    percentage:       currentSubjectData.percentage,
    topicBreakdown:   currentSubjectData.topicBreakdown,
    comparative:      currentSubjectData.comparative,
    questionAnalysis: currentSubjectData.questionAnalysis,
    errors:           currentSubjectData.errors,
    feedback:         grading?.feedback,
  } : (grading || {});

  const percentage  = eg.percentage || exam?.percentage || 0;
  const scoreColor  = eg.pass ? '#43AA8B' : '#DC143C';
  const mainColor   = '#43AA8B';
  const totalScore  = currentSubjectData ? (currentSubjectData.totalScore ?? null) : (exam?.totalScore ?? null);

  const [isQuestionExpanded, setIsQuestionExpanded] = useState(false);
  const [isErrorExpanded,    setIsErrorExpanded]    = useState(false);

  const hasManyQuestions = (eg.questionAnalysis?.length || 0) > 3;
  const hasManyErrors    = (eg.errors?.length || 0) > 3;

  const COLLAPSED_Q_HEIGHT  = 280;
  const COLLAPSED_ERR_HEIGHT = 230;

  const totalTime = (eg.questionAnalysis || []).reduce(
    (acc, q) => acc + (parseInt(q.time) || 0), 0
  );

  // Always use the computed percentage for "Your Performance" — the AI puts the raw score
  // in comparative.user which is unreliable; percentage is definitively correct.
  const peerDiff = eg.comparative
    ? (percentage - (eg.comparative.average || 0)).toFixed(1)
    : null;

  const renderExpandPill = (expanded, onClick) => (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '6px 14px', borderRadius: 999,
        border: '1px solid rgba(95,190,197,0.6)',
        background: 'linear-gradient(90deg, rgba(95,190,197,0.1), rgba(162,89,255,0.14))',
        color: '#0f3135', fontSize: '0.78rem', fontWeight: 600,
        letterSpacing: '0.06em', textTransform: 'uppercase',
        cursor: 'pointer', outline: 'none', whiteSpace: 'nowrap',
        boxShadow: '0 8px 20px rgba(95,190,197,0.25)',
        backdropFilter: 'blur(10px)',
      }}
    >
      <span>See all</span>
      <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 18, height: 18, borderRadius: '50%',
        background: 'rgba(255,255,255,0.9)', color: '#3E8F93',
      }}>
        {expanded ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
      </span>
    </button>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* ── Subject pills (multi-subject exams only) ── */}
      {isMulti && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <button
            type="button"
            onClick={() => setActiveSubjectIdx('all')}
            style={{
              padding: '6px 14px', borderRadius: 999, fontSize: '0.78rem', fontWeight: 700,
              cursor: 'pointer', whiteSpace: 'nowrap',
              border: activeSubjectIdx === 'all' ? '1px solid #5fbec5' : '1px solid rgba(0,0,0,0.12)',
              background: activeSubjectIdx === 'all' ? '#5fbec5' : '#fff',
              color: activeSubjectIdx === 'all' ? '#fff' : '#455a64',
            }}
          >
            All Subjects
          </button>
          {subjectBreakdown.map(s => (
            <button
              key={s.subjectIdx}
              type="button"
              onClick={() => setActiveSubjectIdx(s.subjectIdx)}
              style={{
                padding: '6px 14px', borderRadius: 999, fontSize: '0.78rem', fontWeight: 700,
                cursor: 'pointer', whiteSpace: 'nowrap',
                border: activeSubjectIdx === s.subjectIdx ? '1px solid #5fbec5' : '1px solid rgba(0,0,0,0.12)',
                background: activeSubjectIdx === s.subjectIdx ? '#5fbec5' : '#fff',
                color: activeSubjectIdx === s.subjectIdx ? '#fff' : '#455a64',
              }}
            >
              {s.subjectName}
            </button>
          ))}
        </div>
      )}

      {/* ── Stat cards ── */}
      <StatGrid>
        {/* Total Score */}
        <StatCard iconColor={scoreColor}>
          <div className="icon"><FaTrophy /></div>
          <ScoreCircleWrapper percentage={percentage} color={scoreColor}>
            <svg width="150" height="150">
              <circle className="bg"       cx="75" cy="75" r="70" />
              <circle className="progress" cx="75" cy="75" r="70" />
            </svg>
            <ScoreText>
              <span className="value" style={{ color: scoreColor }}>{eg.score}</span>
              <span className="label">Total Score</span>
            </ScoreText>
          </ScoreCircleWrapper>
          <div style={{ marginTop: '0.75rem' }}>
            <Badge variant={eg.pass ? 'success' : 'danger'}>
              {eg.pass ? 'Passed' : 'Failed'}
            </Badge>
          </div>
        </StatCard>

        {/* Accuracy Rate */}
        <StatCard iconColor={mainColor}>
          <div className="icon"><FaChartLine /></div>
          <div className="value">{percentage}%</div>
          <div className="label">Accuracy Rate</div>
          <div style={{ marginTop: 'auto', width: '100%' }}>
            <ProgressBarTrack height="6px">
              <ProgressBarFill progress={percentage} color={mainColor} />
            </ProgressBarTrack>
          </div>
        </StatCard>

        {/* Total Time */}
        <StatCard iconColor={mainColor}>
          <div className="icon"><FaHourglass /></div>
          <div className="value">{totalTime}s</div>
          <div className="label">Total Time Spent</div>
        </StatCard>

        {/* vs Peer Average */}
        <StatCard iconColor={mainColor}>
          <div className="icon"><FaUsers /></div>
          <div className="value">
            {peerDiff !== null ? `${peerDiff > 0 ? '+' : ''}${peerDiff}%` : '—'}
          </div>
          <div className="label">vs. Peer Average</div>
        </StatCard>

        {/* Total Attainable Score */}
        {totalScore != null && (
          <StatCard iconColor="#6d4c41">
            <div className="icon"><FaTrophy style={{ color: '#6d4c41' }} /></div>
            <div className="value" style={{ color: '#6d4c41' }}>{totalScore}</div>
            <div className="label">Total Attainable</div>
          </StatCard>
        )}
      </StatGrid>

      {/* ── Topic & Comparative ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem' }}>
        {/* Topic Performance */}
        {(eg.topicBreakdown || []).length > 0 && (
          <GlassCard style={{ flex: '1 1 320px' }}>
            <h6 style={{ margin: '0 0 1.25rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
              <FaChartPie color="#43AA8B" /> Topic Performance{currentSubjectData ? ` — ${currentSubjectData.subjectName}` : ''}
            </h6>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {eg.topicBreakdown.map((t, i) => {
                const pct = t.total > 0 ? Math.round((t.correct / t.total) * 100) : 0;
                return (
                  <div key={i}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ fontSize: '0.88rem', fontWeight: 600 }}>{t.topic}</span>
                      <span style={{ fontSize: '0.88rem', opacity: 0.8 }}>{t.correct}/{t.total} ({pct}%)</span>
                    </div>
                    <ProgressBarTrack>
                      <ProgressBarFill progress={pct} />
                    </ProgressBarTrack>
                  </div>
                );
              })}
            </div>
          </GlassCard>
        )}

        {/* Comparative Analysis */}
        {eg.comparative && (
          <GlassCard style={{ flex: '1 1 240px' }}>
            <h6 style={{ margin: '0 0 1.25rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
              <FaUsers color="#43AA8B" /> Comparative Analysis{currentSubjectData ? ` — ${currentSubjectData.subjectName}` : ''}
            </h6>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', paddingTop: '0.5rem' }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>Your Performance</span>
                  <span style={{ color: '#43AA8B', fontWeight: 700 }}>{percentage}%</span>
                </div>
                <ProgressBarTrack height="12px">
                  <ProgressBarFill progress={percentage} color="#43AA8B" />
                </ProgressBarTrack>
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>Peer Average</span>
                  <span style={{ color: '#FF6B6B', fontWeight: 700 }}>{eg.comparative.average}%</span>
                </div>
                <ProgressBarTrack height="12px">
                  <ProgressBarFill progress={eg.comparative.average} color="#FF6B6B" />
                </ProgressBarTrack>
              </div>
            </div>
          </GlassCard>
        )}
      </div>

      {/* ── Question-Level Analysis ── */}
      {(eg.questionAnalysis || []).length > 0 && (
        <GlassCard>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '1.25rem' }}>
            <h6 style={{ margin: 0, fontWeight: 700 }}>Question-Level Analysis{currentSubjectData ? ` — ${currentSubjectData.subjectName}` : ''}</h6>
            {hasManyQuestions && renderExpandPill(isQuestionExpanded, () => setIsQuestionExpanded(p => !p))}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <div style={{
              maxHeight: hasManyQuestions && !isQuestionExpanded ? COLLAPSED_Q_HEIGHT : 'none',
              overflow: hasManyQuestions && !isQuestionExpanded ? 'hidden' : 'visible',
              transition: 'max-height 0.35s ease',
            }}>
              <table className={css.table} style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                    {['#', 'Question', 'Result', 'Selected', 'Correct', 'Score', 'Time', 'Topic'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '10px 12px', whiteSpace: 'nowrap', fontWeight: 700, color: '#607d8b' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {eg.questionAnalysis.map((q, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid rgba(0,0,0,0.03)' }}>
                      <td style={{ padding: '10px 12px', fontWeight: 700 }}>{q.number}</td>
                      <td style={{ padding: '10px 12px', minWidth: 180, maxWidth: 300, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {q.question || q.question_text || '—'}
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        {q.correct
                          ? <FaCheckCircle color="#43AA8B" size={16} />
                          : <FaTimesCircle color="#FF6B6B" size={16} />}
                      </td>
                      <td style={{ padding: '10px 12px', color: q.correct ? '#43AA8B' : '#FF6B6B', fontWeight: 600 }}>{q.chose || '—'}</td>
                      <td style={{ padding: '10px 12px', color: '#43AA8B', fontWeight: 600 }}>{q.right || '—'}</td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontWeight: 600 }}>
                        {q.scoreAwarded != null && q.maxScore != null ? `${q.scoreAwarded}/${q.maxScore}` : '—'}
                      </td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{q.time ?? '—'}s</td>
                      <td style={{ padding: '10px 12px' }}>
                        {q.topic ? (
                          <Badge variant="success" style={{ background: 'rgba(62,143,147,0.1)', color: '#3E8F93', border: 'none', fontSize: '0.7rem' }}>
                            {q.topic}
                          </Badge>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </GlassCard>
      )}

      {/* ── Error Analysis ── */}
      <GlassCard>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '1rem' }}>
          <h6 style={{ margin: 0, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, color: '#FF6B6B' }}>
            <FaRegBookmark /> Error Analysis{currentSubjectData ? ` — ${currentSubjectData.subjectName}` : ''}
          </h6>
          {hasManyErrors && renderExpandPill(isErrorExpanded, () => setIsErrorExpanded(p => !p))}
        </div>
        {(eg.errors || []).length === 0 ? (
          <div style={{ textAlign: 'center', padding: '1rem' }}>
            <div style={{ fontSize: '2rem' }}>🎉</div>
            <p style={{ margin: '8px 0 0', color: '#43AA8B', fontWeight: 600 }}>Perfect score! No errors found.</p>
          </div>
        ) : (
          <div style={{
            maxHeight: hasManyErrors && !isErrorExpanded ? COLLAPSED_ERR_HEIGHT : 'none',
            overflow: hasManyErrors && !isErrorExpanded ? 'hidden' : 'visible',
            transition: 'max-height 0.35s ease',
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {eg.errors.map((e, i) => (
                <div key={i} style={{ padding: '10px 14px', borderRadius: 12, background: 'rgba(255,107,107,0.05)', border: '1px solid rgba(255,107,107,0.12)' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.88rem', marginBottom: 4 }}>Question {e.question}</div>
                  <div style={{ fontSize: '0.83rem', color: '#555' }}>{e.mistake}</div>
                  <div style={{ fontSize: '0.79rem', fontStyle: 'italic', marginTop: 4, color: '#777' }}>{e.explanation}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </GlassCard>

      {/* ── BubbleAI Feedback ── */}
      {grading.feedback && (
        <GlassCard style={{ borderLeft: '4px solid #A259FF' }}>
          <h6 style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 0.75rem', fontWeight: 700 }}>
            <FaRobot color="#A259FF" /> BubbleAI Feedback Summary
          </h6>
          <p style={{ margin: 0, fontSize: '0.88rem', lineHeight: '1.65', color: '#444' }}>{grading.feedback}</p>
        </GlassCard>
      )}
    </div>
  );
}

// ─── PracticeExamModal ────────────────────────────────────────────────────────
// Standalone practice modal: shows AI-generated questions, timer, and client-
// side graded results with explanations. No server attempt is created.
function PracticeExamModal({ title, topic, mockMins, questions, loading, error, onClose, schoolExamId, onComplete }) {
  const [phase, setPhase]         = useState('taking'); // 'taking' | 'results'
  const [answers, setAnswers]     = useState({});       // { question_number: chosenOption }
  const [currentIdx, setCurrentIdx] = useState(0);
  const [timeLeft, setTimeLeft]   = useState((mockMins || 10) * 60);
  const [results, setResults]     = useState(null);     // computed on submit
  const [showConfirm, setShowConfirm] = useState(false);

  // Timer countdown (only while taking)
  useEffect(() => {
    if (phase !== 'taking' || loading || !questions) return;
    if (timeLeft <= 0) { handleSubmit(); return; }
    const t = setInterval(() => setTimeLeft(s => {
      if (s <= 1) { clearInterval(t); return 0; }
      return s - 1;
    }), 1000);
    return () => clearInterval(t);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  const fmt = (s) => {
    const m = Math.floor(s / 60), sec = s % 60;
    return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  };

  const handleSubmit = async () => {
    if (!questions) return;
    let correct = 0;
    const detail = questions.map(q => {
      const chosen = answers[q.question_number] || null;
      const isCorrect = chosen === q.correct_answer;
      if (isCorrect) correct++;
      return { ...q, chosen, isCorrect };
    });
    const pct = Math.round((correct / questions.length) * 100);
    const elapsed = (mockMins || 10) * 60 - timeLeft;
    setResults({ correct, total: questions.length, pct, passed: pct >= 50, detail });
    setPhase('results');
    setShowConfirm(false);
    // Persist the result before completing the linked task so result viewers
    // become available immediately and never rely on task.done as a proxy.
    if (schoolExamId) {
      try {
        await studentFetch('/student/study-plan-mock-result', {
          method: 'POST',
          body: JSON.stringify({
            schoolExamId,
            topic: topic || '',
            score: correct,
            total: questions.length,
            pct,
            passed: pct >= 50,
            timeTaken: elapsed,
            questions: detail,
          }),
        });
      } catch {}
    }
    // Auto-mark the linked study plan task as complete on mock completion
    if (onComplete) onComplete();
  };

  const current = questions?.[currentIdx];
  const answered = Object.keys(answers).length;

  return (
    <div className={css.practiceOverlay}>
      <div className={`${css.practiceBox} BubblePopIn`}>

        {/* ── Header ────────────────────────────────────────────────────────── */}
        <div className={css.practiceHeader}>
          <div>
            <span className={css.practiceBadge}>Practice Mock</span>
            <h2 className={css.practiceHeaderTitle}>{topic || title}</h2>
            {title && topic && title !== topic && (
              <p className={css.practiceHeaderSub}>{title}</p>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0 }}>
            {phase === 'taking' && !loading && (
              <span className={`${css.practiceTimer} ${timeLeft < 60 ? css.practiceTimerWarn : ''}`}>
                ⏱ {fmt(timeLeft)}
              </span>
            )}
            <button className={css.spModalClose} onClick={onClose}>✕</button>
          </div>
        </div>

        {/* ── Loading ────────────────────────────────────────────────────────── */}
        {loading && (
          <div className={css.practiceLoading}>
            <span className={css.btnSpinner} style={{ width: 36, height: 36, borderWidth: 4, borderColor: 'rgba(95,190,197,0.3)', borderTopColor: '#5fbec5' }} />
            <p style={{ margin: '18px 0 4px', fontWeight: 700, color: '#0c1e28' }}>Generating practice questions…</p>
            <p style={{ margin: 0, fontSize: '0.83rem', color: '#90a4ae' }}>Creating {Math.round(mockMins / 2)} questions on <em>{topic}</em></p>
          </div>
        )}

        {/* ── Error ──────────────────────────────────────────────────────────── */}
        {!loading && error && (
          <div className={css.practiceLoading}>
            <span style={{ fontSize: '2rem' }}>⚠️</span>
            <p style={{ margin: '12px 0 4px', fontWeight: 700, color: '#dc2626' }}>Failed to generate questions</p>
            <p style={{ margin: '0 0 20px', fontSize: '0.83rem', color: '#90a4ae' }}>{error}</p>
            <button className={css.outlineBtn} onClick={onClose}>Close</button>
          </div>
        )}

        {/* ── Taking phase ───────────────────────────────────────────────────── */}
        {!loading && !error && questions && phase === 'taking' && (
          <div className={css.practiceBody}>
            {/* Left: question grid + progress */}
            <div className={css.practiceSidebar}>
              <p style={{ margin: '0 0 10px', fontSize: '0.73rem', fontWeight: 700, color: '#607d8b', textTransform: 'uppercase' }}>
                Questions
              </p>
              <div className={css.practiceQGrid}>
                {questions.map((q, i) => (
                  <button
                    key={q.question_number}
                    className={`${css.practiceQDot} ${i === currentIdx ? css.practiceQDotActive : ''} ${answers[q.question_number] ? css.practiceQDotAnswered : ''}`}
                    onClick={() => setCurrentIdx(i)}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
              <div className={css.practiceSidebarStats}>
                <div><span className={css.practiceSidebarStatVal}>{answered}</span><span className={css.practiceSidebarStatLbl}>answered</span></div>
                <div><span className={css.practiceSidebarStatVal}>{questions.length - answered}</span><span className={css.practiceSidebarStatLbl}>remaining</span></div>
              </div>
              <button
                className={css.primaryBtn}
                style={{ width: '100%', marginTop: 16 }}
                onClick={() => setShowConfirm(true)}
              >
                Submit Mock
              </button>
            </div>

            {/* Right: current question */}
            <div className={css.practiceQBlock}>
              {current && (
                <>
                  <p className={css.practiceQNum}>Question {currentIdx + 1} of {questions.length}</p>
                  {current.instructions && (
                    <p style={{ fontSize: '0.8rem', color: '#90a4ae', margin: '0 0 8px', fontStyle: 'italic' }}>{current.instructions}</p>
                  )}
                  <p className={css.practiceQText}>{current.question_text}</p>
                  <div className={css.practiceOptions}>
                    {current.options.map((opt, oi) => {
                      const selected = answers[current.question_number] === opt;
                      return (
                        <button
                          key={oi}
                          className={`${css.practiceOption} ${selected ? css.practiceOptionSelected : ''}`}
                          onClick={() => setAnswers(a => ({ ...a, [current.question_number]: opt }))}
                        >
                          <span className={css.practiceOptionLetter}>{String.fromCharCode(65 + oi)}</span>
                          <span>{opt}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div className={css.practiceNav}>
                    <button
                      className={css.outlineBtn}
                      disabled={currentIdx === 0}
                      onClick={() => setCurrentIdx(i => i - 1)}
                    >← Prev</button>
                    {currentIdx < questions.length - 1 ? (
                      <button className={css.primaryBtn} onClick={() => setCurrentIdx(i => i + 1)}>Next →</button>
                    ) : (
                      <button className={css.primaryBtn} onClick={() => setShowConfirm(true)}>Finish &amp; Submit</button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ── Results phase ──────────────────────────────────────────────────── */}
        {phase === 'results' && results && (
          <div className={css.practiceResults}>
            {/* Score summary */}
            <div className={`${css.practiceScoreCard} ${results.passed ? css.practiceScorePass : css.practiceScoreFail}`}>
              <div className={css.practiceScoreMain}>
                <span className={css.practiceScorePct}>{results.pct}%</span>
                <span className={`${css.chip} ${results.passed ? css.chipGreen : css.chipRed}`} style={{ marginLeft: 12 }}>
                  {results.passed ? 'Pass' : 'Needs Work'}
                </span>
              </div>
              <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: '#607d8b' }}>
                {results.correct} of {results.total} correct · {topic}
              </p>
            </div>

            {/* Per-question breakdown */}
            <div className={css.practiceResultList}>
              {results.detail.map((q, i) => (
                <div key={i} className={`${css.practiceResultItem} ${q.isCorrect ? css.practiceResultCorrect : css.practiceResultWrong}`}>
                  <div className={css.practiceResultHeader}>
                    <span className={css.practiceResultBadge}>{q.isCorrect ? '✓' : '✗'}</span>
                    <p className={css.practiceResultQ}><strong>Q{i + 1}.</strong> {q.question_text}</p>
                  </div>
                  <div className={css.practiceResultAnswers}>
                    {!q.isCorrect && q.chosen && (
                      <span className={css.practiceResultYours}>Your answer: {q.chosen}</span>
                    )}
                    <span className={css.practiceResultCorrectAns}>Correct: {q.correct_answer}</span>
                  </div>
                  {q.explanation && (
                    <p className={css.practiceResultExplanation}>{q.explanation}</p>
                  )}
                </div>
              ))}
            </div>

            <div style={{ padding: '16px 24px 24px', textAlign: 'center' }}>
              <button className={css.outlineBtn} onClick={onClose}>Close Practice</button>
            </div>
          </div>
        )}

        {/* ── Submit confirm overlay ─────────────────────────────────────────── */}
        {showConfirm && (
          <div className={css.spModalOverlay} onClick={() => setShowConfirm(false)}>
            <div className={`${css.spModalBox} BubblePopIn`} style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
              <div className={css.spModalHeader}>
                <h3 className={css.spModalTitle}>Submit Practice Mock?</h3>
                <button className={css.spModalClose} onClick={() => setShowConfirm(false)}>✕</button>
              </div>
              <div style={{ padding: '16px 24px' }}>
                <p style={{ margin: '0 0 6px', fontSize: '0.88rem', color: '#263238' }}>
                  You've answered <strong>{answered}</strong> of <strong>{questions?.length || 0}</strong> questions.
                </p>
                {answered < (questions?.length || 0) && (
                  <p style={{ margin: 0, fontSize: '0.82rem', color: '#f59e0b' }}>
                    ⚠ Unanswered questions will be marked incorrect.
                  </p>
                )}
              </div>
              <div style={{ display: 'flex', gap: 10, padding: '0 24px 24px', justifyContent: 'flex-end' }}>
                <button className={css.outlineBtn} onClick={() => setShowConfirm(false)}>Keep Going</button>
                <button className={css.primaryBtn} onClick={handleSubmit}>Submit</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── InlineInsightPanel ───────────────────────────────────────────────────────
function InlineInsightPanel({ exam, defaultTab = 'grading', studyPlan, studyLoading, studyError, onToggleTask, onStartMock, navigate, resultsReleased = true }) {
  const [insightTab, setInsightTab] = useState(defaultTab);

  // Sync when defaultTab changes (e.g. user clicks a different action button)
  useEffect(() => { setInsightTab(defaultTab); }, [defaultTab]);
  const [grading, setGrading]           = useState(null);
  const [gLoading, setGLoading]         = useState(true);
  const [resultTotalScore, setResultTotalScore] = useState(null);
  const [subjectBreakdown, setSubjectBreakdown] = useState([]);
  const [activeSubjectIdx, setActiveSubjectIdx] = useState('all');
  // Separate subject filter for the Study Plan tab (independent of the
  // Grading tab's activeSubjectIdx above)
  const [spSubjectIdx, setSpSubjectIdx] = useState('all');

  // ── Video modal state ──────────────────────────────────────────────────────
  const [vidModal, setVidModal] = useState({
    open: false, task: null, query: '', results: [],
    selectedIdx: 0, selectedVideoId: null,
    loading: false, error: null,
    duration: 'long', country: '',
    iframeLoading: false,
  });

  // ── Note modal state ───────────────────────────────────────────────────────
  const [noteModal, setNoteModal] = useState({
    open: false, task: null, loading: false, content: '',
    title: '', error: null, saved: false, noteId: null,
    fullShown: false, saving: false,
  });
  const [noteCache, setNoteCache] = useState({});

  // ── Mock confirm state ─────────────────────────────────────────────────────
  const [mockConfirm, setMockConfirm] = useState({ open: false, task: null });
  const [mockResultModal, setMockResultModal] = useState({ open: false, task: null, loading: false, content: null, error: '' });
  const [mockResultTopics, setMockResultTopics] = useState(() => new Set());

  const handleViewMockResult = useCallback(async task => {
    const topic = task?.resource?.title || task?.title || '';
    setMockResultModal({ open: true, task, loading: true, content: null, error: '' });
    try {
      const response = await studentFetch(`/student/study-plan-mock-result?schoolExamId=${exam.schoolExamId}&topic=${encodeURIComponent(topic)}`);
      const data = await response.json();
      setMockResultModal(current => ({
        ...current,
        loading: false,
        content: response.ok ? data.result : null,
        error: response.ok ? '' : (data.error || 'Failed to load practice result'),
      }));
    } catch {
      setMockResultModal(current => ({ ...current, loading: false, error: 'Failed to load practice result' }));
    }
  }, [exam?.schoolExamId]);

  // ── Video handlers ─────────────────────────────────────────────────────────
  const fetchYoutube = useCallback(async (query, duration, country) => {
    setVidModal(prev => ({ ...prev, loading: true, error: null }));
    try {
      let url = `/student/youtube-search?q=${encodeURIComponent(query)}&maxResults=3&videoDuration=${duration || 'long'}`;
      if (country) url += `&regionCode=${country}`;
      const r = await studentFetch(url);
      const d = await r.json();
      const items = d.items || [];
      setVidModal(prev => ({
        ...prev, results: items, loading: false,
        selectedIdx: 0,
        selectedVideoId: items[0]?.videoId || null,
        iframeLoading: !!items[0]?.videoId,
      }));
    } catch {
      setVidModal(prev => ({ ...prev, loading: false, error: 'Could not load videos.' }));
    }
  }, []);

  const handleVideoClick = useCallback(async (task, flatIdx) => {
    const res = task.resource || {};
    const query = res.title || task.title || '';
    setVidModal({ open: true, task, query, results: [], selectedIdx: 0, selectedVideoId: null, loading: true, error: null, duration: 'long', country: '', iframeLoading: false });
    fetchYoutube(query, 'long', '');
    // Auto-mark complete on first watch (still manually untickable afterward)
    if (!task.done && onToggleTask && flatIdx != null) onToggleTask(flatIdx, false);
  }, [fetchYoutube, onToggleTask]);

  // ── Note handlers ──────────────────────────────────────────────────────────
  const handleNoteClick = useCallback(async (task, flatIdx) => {
    const res = task.resource || {};
    const title = res.title || task.title || 'Study Note';
    const examObjectId = String(exam.examId);
    const cacheKey = `${examObjectId}::${title}`;

    setNoteModal({ open: true, task, loading: true, content: '', title, error: null, saved: false, noteId: null, fullShown: false, saving: false });
    // Auto-mark complete on first view (still manually untickable afterward)
    if (!task.done && onToggleTask && flatIdx != null) onToggleTask(flatIdx, false);

    if (noteCache[cacheKey]) {
      const cached = noteCache[cacheKey];
      setNoteModal(prev => ({ ...prev, loading: false, content: cached.content, saved: true, noteId: cached.noteId || null }));
      return;
    }

    try {
      const r = await studentFetch('/student/study-plan/generate-note', {
        method: 'POST',
        body: JSON.stringify({
          examObjectId,
          resource: { title, type: res.type || res.resourceType || 'Notes', prompt: res.prompt || `Generate comprehensive notes for "${title}"` },
        }),
      });
      const d = await r.json();
      const content = d.note || '';
      if (!content || content.length < 30) throw new Error('Empty note');
      const nid = d.noteId ? String(d.noteId) : null;
      setNoteModal(prev => ({ ...prev, loading: false, content, saved: true, noteId: nid }));
      setNoteCache(prev => ({ ...prev, [cacheKey]: { content, noteId: nid } }));
    } catch {
      setNoteModal(prev => ({ ...prev, loading: false, error: 'Failed to generate note. Please try again.' }));
    }
  }, [exam?.examId, noteCache, onToggleTask]);

  const handleNoteSave = useCallback(async () => {
    if (noteModal.saving) return;
    if (noteModal.saved && noteModal.noteId) {
      setNoteModal(prev => ({ ...prev, saving: true }));
      try {
        await studentFetch(`/student/studyplan-notes/${noteModal.noteId}`, { method: 'DELETE' });
        setNoteModal(prev => ({ ...prev, saved: false, saving: false, noteId: null }));
        const cacheKey = `${exam.examId}::${noteModal.title}`;
        setNoteCache(prev => ({ ...prev, [cacheKey]: { ...(prev[cacheKey] || {}), noteId: null } }));
      } catch {
        setNoteModal(prev => ({ ...prev, saving: false }));
      }
    }
  }, [noteModal, exam?.examId]);

  useEffect(() => {
    if (!exam?.schoolExamId) return;
    setActiveSubjectIdx('all');
    setSpSubjectIdx('all');
    studentFetch(`/student/exams/${exam.schoolExamId}/result`).then(r => r.json()).then(d => {
      setGrading(d.result?.grading || null);
      if (d.result?.totalScore != null) setResultTotalScore(d.result.totalScore);
      setSubjectBreakdown(d.result?.subjectBreakdown || []);
      setGLoading(false);
    }).catch(() => setGLoading(false));
  }, [exam?.schoolExamId]);

  // Flatten study plan tasks for flat-index toggle
  const allTasks = (studyPlan?.schedule || []).flatMap(d => d.tasks || []);

  useEffect(() => {
    if (!exam?.schoolExamId || !studyPlan?.schedule) return;
    let active = true;
    const mockTopics = allTasks
      .filter(task => {
        const resourceType = (task.resource?.resourceType || task.resource?.type || '').toLowerCase();
        return resourceType.includes('mock');
      })
      .map(task => task.resource?.title || task.title || '')
      .filter(Boolean);

    Promise.all(mockTopics.map(async topic => {
      try {
        const response = await studentFetch(`/student/study-plan-mock-result?schoolExamId=${exam.schoolExamId}&topic=${encodeURIComponent(topic)}`);
        const data = await response.json();
        return data.result ? topic : null;
      } catch {
        return null;
      }
    })).then(topics => {
      if (active) setMockResultTopics(new Set(topics.filter(Boolean)));
    });
    return () => { active = false; };
  }, [exam?.schoolExamId, studyPlan]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ padding: '16px 24px 24px', borderTop: '2px solid #5fbec5' }}>
      <div className={css.tabs} style={{ marginBottom: 16 }}>
        {[
          { k: 'grading', l: 'Grading', Icon: CheckCircleIcon },
          { k: 'studyplan', l: 'Study Plan', Icon: MenuBookIcon },
          { k: 'tutor', l: 'AI Tutor', Icon: SmartToyIcon },
        ].map(({ k, l, Icon }) => (
          <button key={k} className={`${css.tab} ${insightTab === k ? css.tabActive : ''}`} onClick={() => setInsightTab(k)}>
            <Icon style={{ fontSize: '1rem', marginRight: 5, verticalAlign: 'middle' }} />{l}
          </button>
        ))}
      </div>

      {/* Grading tab */}
      {insightTab === 'grading' && (
        <div>
          {gLoading ? (
            <div style={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div className={css.skel} style={{ width: '100%', height: 80 }} />
            </div>
          ) : !grading ? (
            <div className={css.emptyState}>
              <p>Grading data not available yet.</p>
            </div>
          ) : (
            <GradingDetails
              grading={grading}
              exam={{ ...exam, totalScore: resultTotalScore ?? exam?.totalScore ?? null }}
              isMultiSubject={exam?.isMultiSubject}
              subjectBreakdown={subjectBreakdown}
              activeSubjectIdx={activeSubjectIdx}
              setActiveSubjectIdx={setActiveSubjectIdx}
            />
          )}
        </div>
      )}

      {/* Study Plan tab */}
      {insightTab === 'studyplan' && (
        <div>
          {!resultsReleased ? (
            <div className={css.emptyState} style={{ padding: '28px 0' }}>
              <span style={{ fontSize: '1.6rem' }}>🔒</span>
              <p style={{ margin: '10px 0 4px', fontWeight: 700, color: '#455a64' }}>Results not yet released</p>
              <p style={{ margin: 0, fontSize: '0.83rem', color: '#90a4ae' }}>Your study plan will be generated once your teacher or exam deadline releases the results.</p>
            </div>
          ) : studyLoading ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: '#90a4ae' }}>
              <div className={css.skel} style={{ width: '100%', height: 120 }} />
              <p style={{ marginTop: 12 }}>Generating your personalised study plan…</p>
            </div>
          ) : studyError ? (
            <div className={css.emptyState}><ErrorIcon style={{ color: '#dc2626' }} /><p>{studyError}</p></div>
          ) : !studyPlan ? (
            <div className={css.emptyState}><p>Study plan not ready yet.</p></div>
          ) : studyPlan._perfect ? (
            <div className={css.emptyState} style={{ padding: '28px 0', textAlign: 'center' }}>
              <span style={{ fontSize: '2.2rem' }}>🏆</span>
              <p style={{ margin: '10px 0 4px', fontWeight: 700, color: '#0c7c84', fontSize: '1rem' }}>Perfect Score!</p>
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#455a64' }}>{studyPlan.message}</p>
            </div>
          ) : !resultsReleased ? null : (
            <div>
              {(() => {
                const spSubjects = Array.from(
                  new Map(allTasks.filter(t => typeof t.subjectIdx === 'number').map(t => [t.subjectIdx, t.subjectName || `Subject ${t.subjectIdx + 1}`])).entries()
                ).sort((a, b) => a[0] - b[0]);
                const showSpTabs = exam?.isMultiSubject && spSubjects.length > 1;
                const selectedSpSubjectIdx = showSpTabs && spSubjectIdx === 'all' ? spSubjects[0][0] : spSubjectIdx;
                const visibleTasks = showSpTabs
                  ? allTasks.filter(t => t.subjectIdx === selectedSpSubjectIdx)
                  : allTasks;
                return (
                  <>
                    {showSpTabs && (
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                        {spSubjects.map(([idx, name]) => (
                          <button
                            key={idx}
                            className={`${css.tab} ${selectedSpSubjectIdx === idx ? css.tabActive : ''}`}
                            style={{ padding: '6px 14px', borderRadius: 20, fontSize: '0.78rem' }}
                            onClick={() => setSpSubjectIdx(idx)}
                          >
                            {name}
                          </button>
                        ))}
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                      <span style={{ fontSize: '0.8rem', color: '#90a4ae' }}>
                        {visibleTasks.filter(t => t.done).length} / {visibleTasks.length} tasks done
                      </span>
                      <div className={css.progressTrack} style={{ width: 140 }}>
                        <div className={css.progressFill} style={{ width: `${visibleTasks.length ? Math.round((visibleTasks.filter(t=>t.done).length/visibleTasks.length)*100) : 0}%` }} />
                      </div>
                    </div>
                  </>
                );
              })()}

              {(studyPlan.schedule || []).map((day, di) => {
                const selectedIdx = exam?.isMultiSubject && spSubjectIdx === 'all'
                  ? allTasks.find(t => typeof t.subjectIdx === 'number')?.subjectIdx
                  : spSubjectIdx;
                const dayHasVisible = (day.tasks || []).some(t => selectedIdx === 'all' || t.subjectIdx === selectedIdx);
                if (!dayHasVisible) return null;
                return (
                <div key={day.date} style={{ marginBottom: 16 }}>
                  <p style={{ margin: '0 0 8px', fontSize: '0.8rem', fontWeight: 700, color: '#607d8b' }}>{day.date}</p>
                  {(day.tasks || []).map((task, ti) => {
                    const flatIdx = (studyPlan.schedule || []).slice(0, di).reduce((acc, d) => acc + (d.tasks?.length || 0), 0) + ti;
                    if (selectedIdx !== 'all' && task.subjectIdx !== selectedIdx) return null;
                    return (
                      <div key={ti} className={`${css.taskRow} ${task.done ? css.taskDone : ''}`}>
                        <div
                          className={`${css.taskCheck} ${task.done ? css.taskCheckDone : ''}`}
                          onClick={() => onToggleTask(flatIdx, task.done)}
                          role="checkbox"
                          aria-checked={task.done}
                        >
                          {task.done && <CheckCircleIcon style={{ fontSize: '0.85rem', color: '#fff' }} />}
                        </div>
                        <div style={{ flex: 1 }}>
                          <p className={css.taskTitle}>{task.title}</p>
                          <p style={{ margin: 0, fontSize: '0.75rem', color: '#90a4ae' }}>
                            {task.timeAllocation} min
                            {task.importance >= 4 && <span style={{ marginLeft: 6, color: '#f59e0b' }}>★ High priority</span>}
                          </p>
                        </div>
                        {(() => {
                          const rType = (typeof task.resource === 'string' ? '' : (task.resource?.resourceType || task.resource?.type || '')).toLowerCase();
                          const baseType = rType.includes('video') ? 'video' : rType.includes('note') ? 'note' : rType.includes('mock') ? 'mock' : (task.resource ? 'note' : null);
                          if (!baseType) return null;
                          const isDone = task.done;
                          if (baseType === 'video') {
                            return (
                              <button
                                className={isDone ? css.studyTaskCtaReview : css.studyTaskCta}
                                data-cta="video"
                                onClick={() => handleVideoClick(task, flatIdx)}
                              >
                                <FaVideo size={12} /> {isDone ? 'Rewatch' : 'Watch Video'}
                              </button>
                            );
                          }
                          if (baseType === 'note') {
                            return (
                              <button
                                className={isDone ? css.studyTaskCtaReview : css.studyTaskCta}
                                data-cta="note"
                                onClick={() => handleNoteClick(task, flatIdx)}
                              >
                                <FaBookOpen size={12} /> {isDone ? 'Review Notes' : 'AI Notes'}
                              </button>
                            );
                          }
                          // mock → Take Mock confirmation
                          if (baseType === 'mock') {
                            const mockTopic = task.resource?.title || task.title || '';
                            const hasMockResult = mockResultTopics.has(mockTopic);
                            return (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <button
                                  className={isDone ? css.studyTaskCtaReview : css.studyTaskCta}
                                  data-cta="mock"
                                  onClick={() => setMockConfirm({ open: true, task, flatIdx })}
                                >
                                  <FaClipboardList size={12} /> {isDone ? 'Redo Mock' : 'Take Mock'}
                                </button>
                                {hasMockResult && (
                                  <button
                                    className={css.studyTaskCtaReview}
                                    onClick={() => handleViewMockResult(task)}
                                  >
                                    <VisibilityIcon style={{ fontSize: 14 }} /> View
                                  </button>
                                )}
                              </div>
                            );
                          }
                          // other → open AI Tutor
                          return (
                            <button
                              className={isDone ? css.studyTaskCtaReview : css.studyTaskCta}
                              data-cta="tutor"
                              onClick={() => {
                                const context = {
                                  subject: exam.subjectName || exam.title || '',
                                  topic: task.title,
                                  prompt: task.resource?.prompt || `Explain ${task.title}`,
                                  source: 'school_study_plan',
                                };
                                sessionStorage.setItem('bubbleai_tutor_context', JSON.stringify(context));
                                window.open('/chat', '_blank');
                              }}
                            >
                              <FaRobot size={12} /> {isDone ? 'Review' : 'AI Tutor'}
                            </button>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
                );
              })}

            </div>
          )}
        </div>
      )}

      {/* AI Tutor tab */}
      {insightTab === 'tutor' && (
        <div style={{ textAlign: 'center', padding: '32px 0' }}>
          {!resultsReleased ? (
            <>
              <span style={{ fontSize: '1.6rem' }}>🔒</span>
              <p style={{ margin: '12px 0 4px', fontWeight: 700, color: '#455a64' }}>Results not yet released</p>
              <p style={{ margin: 0, fontSize: '0.83rem', color: '#90a4ae', maxWidth: 320, marginLeft: 'auto', marginRight: 'auto' }}>
                The AI Tutor will be available once your teacher or exam deadline releases the results.
              </p>
            </>
          ) : (
            <>
              <SmartToyIcon style={{ fontSize: '2.5rem', color: '#5fbec5', marginBottom: 12 }} />
              <p style={{ margin: '0 0 8px', fontWeight: 700, color: '#0c1e28' }}>AI Study Tutor</p>
              <p style={{ margin: '0 0 20px', color: '#607d8b', fontSize: '0.87rem', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
                Ask the AI tutor anything about this exam's topics. It has full context of your weak areas.
              </p>
              <button
                className={css.primaryBtn}
                onClick={() => {
                  const aiContext = {
                    gradingData: grading,
                    examName: exam.title || exam.subjectName || 'this exam',
                    description: exam.subjectName || '',
                  };
                  sessionStorage.setItem('bubbleai_tutor_context', JSON.stringify(aiContext));
                  window.open('/chat', '_blank');
                }}
              >
                <SmartToyIcon style={{ fontSize: '1rem' }} /> Open AI Tutor
              </button>
            </>
          )}
        </div>
      )}

      {/* ── Video Modal ─────────────────────────────────────────────────────── */}
      {vidModal.open && (
        <div className={css.spModalOverlay} onClick={() => setVidModal(prev => ({ ...prev, open: false }))}>
          <div className={`${css.spModalBox} BubblePopIn`} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className={css.spModalHeader}>
              <div>
                <h3 className={css.spModalTitle}><FaVideo style={{ marginRight: 8, color: '#5fbec5' }} />{vidModal.query || 'Study Videos'}</h3>
                <p className={css.spModalSub}>Recommended videos for this topic</p>
              </div>
              <button className={css.spModalClose} onClick={() => setVidModal(prev => ({ ...prev, open: false }))}>✕</button>
            </div>

            {/* Video area */}
            <div className={css.spVideoWrap}>
              {(vidModal.loading || vidModal.iframeLoading) && (
                <div className={css.spVideoLoader}>
                  <span className={css.btnSpinner} style={{ width: 28, height: 28, borderWidth: 3 }} />
                </div>
              )}
              {vidModal.selectedVideoId && !vidModal.loading ? (
                <iframe
                  key={vidModal.selectedVideoId}
                  src={`https://www.youtube.com/embed/${vidModal.selectedVideoId}`}
                  title="Study video"
                  className={css.spVideoIframe}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  onLoad={() => setVidModal(prev => ({ ...prev, iframeLoading: false }))}
                />
              ) : !vidModal.loading && !vidModal.selectedVideoId && (
                <div className={css.spVideoEmpty}>
                  {vidModal.error || 'No video available. Try a different filter.'}
                </div>
              )}
            </div>

            {/* Filters row */}
            <div className={css.spFiltersRow}>
              <div className={css.spDurationBtns}>
                {[['short', 'Short'], ['medium', 'Medium'], ['long', 'Long']].map(([val, label]) => (
                  <button
                    key={val}
                    className={`${css.spDurBtn} ${vidModal.duration === val ? css.spDurBtnActive : ''}`}
                    onClick={() => {
                      setVidModal(prev => ({ ...prev, duration: val }));
                      fetchYoutube(vidModal.query, val, vidModal.country);
                    }}
                  >{label}</button>
                ))}
              </div>
              <select
                className={css.spCountrySelect}
                value={vidModal.country}
                onChange={e => {
                  const v = e.target.value;
                  setVidModal(prev => ({ ...prev, country: v }));
                  fetchYoutube(vidModal.query, vidModal.duration, v);
                }}
              >
                <option value="">Any Region</option>
                {[['NG','Nigeria'],['GB','United Kingdom'],['US','United States'],['GH','Ghana'],['KE','Kenya'],['ZA','South Africa'],['IN','India'],['CA','Canada'],['AU','Australia'],['EG','Egypt']].map(([code, name]) => (
                  <option key={code} value={code}>{name}</option>
                ))}
              </select>
            </div>

            {/* Thumbnails */}
            {!vidModal.loading && vidModal.results.length > 0 && (
              <div className={css.spThumbRow}>
                {vidModal.results.map((v, idx) => (
                  <div
                    key={v.videoId}
                    className={`${css.spThumb} ${vidModal.selectedIdx === idx ? css.spThumbActive : ''}`}
                    onClick={() => setVidModal(prev => ({ ...prev, selectedIdx: idx, selectedVideoId: v.videoId, iframeLoading: true }))}
                  >
                    <img src={v.thumbnail} alt={v.title} style={{ width: '100%', borderRadius: 6, display: 'block' }} loading="lazy" />
                    <p className={css.spThumbTitle}>{v.title}</p>
                  </div>
                ))}
              </div>
            )}
            <p className={css.spModalHint}>
              Tip: Switch duration (Short/Medium/Long) or region to find the most relevant material for your context.
            </p>
          </div>
        </div>
      )}

      {/* ── Note Modal ──────────────────────────────────────────────────────── */}
      {noteModal.open && (
        <div className={css.spModalOverlay} onClick={() => setNoteModal(prev => ({ ...prev, open: false }))}>
          <div className={`${css.spModalBox} BubblePopIn`} style={{ maxWidth: 620 }} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className={css.spModalHeader}>
              <div>
                <h3 className={css.spModalTitle}><FaBookOpen style={{ marginRight: 8, color: '#5fbec5' }} />{noteModal.title || 'Study Note'}</h3>
                <p className={css.spModalSub}>AI-generated notes for this topic</p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {/* Bookmark save/unsave */}
                {noteModal.content && (
                  <button
                    onClick={handleNoteSave}
                    disabled={noteModal.saving}
                    className={css.spNoteBookmark}
                    title={noteModal.saved ? 'Unsave note' : 'Note saved'}
                  >
                    {noteModal.saving
                      ? <span className={css.btnSpinner} style={{ width: 16, height: 16, borderWidth: 2 }} />
                      : noteModal.saved
                      ? <FaBookmark size={18} color="#5fbec5" />
                      : <FaRegBookmark size={18} color="#90a4ae" />}
                  </button>
                )}
                <button className={css.spModalClose} onClick={() => setNoteModal(prev => ({ ...prev, open: false }))}>✕</button>
              </div>
            </div>

            {/* Body */}
            <div className={css.spNoteBody}>
              {noteModal.loading ? (
                <div className={css.spNoteLoading}>
                  <span className={css.btnSpinner} style={{ width: 32, height: 32, borderWidth: 3, borderColor: '#5fbec5', borderTopColor: 'transparent' }} />
                  <p style={{ margin: '16px 0 0', color: '#607d8b', fontSize: '0.88rem' }}>
                    Generating notes for <strong>{noteModal.title}</strong>…
                  </p>
                </div>
              ) : noteModal.error ? (
                <div style={{ textAlign: 'center', padding: '24px 0', color: '#dc2626' }}>
                  {noteModal.error}
                </div>
              ) : noteModal.content ? (
                <>
                  <div
                    className={css.spNoteContent}
                    style={{
                      maxHeight: !noteModal.fullShown ? 360 : 'none',
                      overflow: !noteModal.fullShown ? 'hidden' : 'visible',
                    }}
                  >
                    {renderMarkdownNote(noteModal.content)}
                  </div>
                  {noteModal.content.split(/\s+/).length > 280 && (
                    <button
                      className={css.spNoteExpand}
                      onClick={() => setNoteModal(prev => ({ ...prev, fullShown: !prev.fullShown }))}
                    >
                      {noteModal.fullShown ? '▲ Show Less' : '▼ Expand Full Note'}
                    </button>
                  )}
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* ── Mock Confirmation Modal ──────────────────────────────────────────── */}
      {mockConfirm.open && (() => {
        const task = mockConfirm.task;
        const rawMins = task?.timeAllocation || 0;
        const mockMins = Math.max(5, Math.round(rawMins * 0.6));
        const reviewMins = rawMins - mockMins;
        const numQ = exam?.settings?.numQuestions || exam?.settings?.questionCount || null;
        return (
          <div className={css.spModalOverlay} onClick={() => setMockConfirm({ open: false, task: null })}>
            <div className={`${css.spModalBox} BubblePopIn`} style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div className={css.spModalHeader}>
                <div>
                  <h3 className={css.spModalTitle}>
                    <FaClipboardList style={{ marginRight: 8, color: '#5fbec5' }} />
                    Practice Mock
                  </h3>
                  <p className={css.spModalSub}>{task?.title}</p>
                </div>
                <button className={css.spModalClose} onClick={() => setMockConfirm({ open: false, task: null })}>✕</button>
              </div>

              {/* Details */}
              <div style={{ padding: '20px 24px 0' }}>
                <div className={css.spMockDetails}>
                  <div className={css.spMockDetail}>
                    <span className={css.spMockDetailLabel}>Topic</span>
                    <span className={css.spMockDetailValue}>{task?.title}</span>
                  </div>
                  <div className={css.spMockDetail}>
                    <span className={css.spMockDetailLabel}>Exam</span>
                    <span className={css.spMockDetailValue}>{exam?.title || exam?.subjectName || '—'}</span>
                  </div>
                  <div className={css.spMockDetail}>
                    <span className={css.spMockDetailLabel}>Mock Duration</span>
                    <span className={css.spMockDetailValue} style={{ color: '#0c7c84', fontWeight: 800 }}>
                      {mockMins} min <span style={{ fontWeight: 400, color: '#90a4ae' }}>(60% of {rawMins} min allocation)</span>
                    </span>
                  </div>
                  {reviewMins > 0 && (
                    <div className={css.spMockDetail}>
                      <span className={css.spMockDetailLabel}>Review Time</span>
                      <span className={css.spMockDetailValue} style={{ color: '#607d8b' }}>{reviewMins} min remaining after mock</span>
                    </div>
                  )}
                  {numQ && (
                    <div className={css.spMockDetail}>
                      <span className={css.spMockDetailLabel}>Questions</span>
                      <span className={css.spMockDetailValue}>{numQ} questions</span>
                    </div>
                  )}
                </div>
                <p className={css.spMockHint}>
                  You'll have <strong>{mockMins} minutes</strong> to complete the mock — use the remaining <strong>{reviewMins} minutes</strong> to review your answers and consolidate what you've learned.
                </p>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 12, padding: '20px 24px 24px', justifyContent: 'flex-end' }}>
                <button
                  className={css.outlineBtn}
                  onClick={() => setMockConfirm({ open: false, task: null })}
                >
                  Cancel
                </button>
                <button
                  className={css.primaryBtn}
                  onClick={() => {
                    setMockConfirm({ open: false, task: null });
                    if (onStartMock) onStartMock(exam, mockMins, task?.resource?.title || task?.title || '', mockConfirm.flatIdx);
                  }}
                >
                  <FaClipboardList style={{ marginRight: 6 }} /> Start Mock
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {mockResultModal.open && (
        <div className={teacherModalCss.tModalOverlay} onClick={() => setMockResultModal({ open: false, task: null, loading: false, content: null, error: '' })}>
          <div className={`${teacherModalCss.tModalBox} BubblePopIn`} onClick={event => event.stopPropagation()}>
            <div className={teacherModalCss.tModalHeader}>
              <div>
                <span className={teacherModalCss.tModalBadge}>🎯 Practice Mock</span>
                <h3 className={teacherModalCss.tModalTitle}>{mockResultModal.task?.title}</h3>
              </div>
              <button className={teacherModalCss.tModalClose} onClick={() => setMockResultModal({ open: false, task: null, loading: false, content: null, error: '' })}>✕</button>
            </div>
            <div className={teacherModalCss.tModalBody}>
              {mockResultModal.loading && <div className={teacherModalCss.tModalLoading}><span className={teacherModalCss.tModalSpinner} /><p>Loading…</p></div>}
              {!mockResultModal.loading && mockResultModal.error && <p className={teacherModalCss.tModalError}>{mockResultModal.error}</p>}
              {!mockResultModal.loading && !mockResultModal.error && (mockResultModal.content ? (
                <div>
                  <div className={teacherModalCss.tModalScoreCard} style={{ borderColor: mockResultModal.content.passed ? '#a5d6a7' : '#ef9a9a', background: mockResultModal.content.passed ? 'linear-gradient(135deg,#e8f5e9,#f1f8e9)' : 'linear-gradient(135deg,#fce4ec,#fef9fa)' }}>
                    <span className={teacherModalCss.tModalScorePct} style={{ color: mockResultModal.content.passed ? '#2e7d32' : '#c62828' }}>{mockResultModal.content.pct}%</span>
                    <span className={teacherModalCss.tModalScoreSub}>{mockResultModal.content.score}/{mockResultModal.content.total} correct · {mockResultModal.content.passed ? 'Pass' : 'Needs Work'}</span>
                    {mockResultModal.content.timeTaken > 0 && <span className={teacherModalCss.tModalScoreSub}>Time: {Math.floor(mockResultModal.content.timeTaken / 60)}m {mockResultModal.content.timeTaken % 60}s</span>}
                  </div>
                  {(mockResultModal.content.questions || []).length > 0 && (
                    <div className={teacherModalCss.tModalQList}>
                      {mockResultModal.content.questions.map((question, index) => (
                        <div key={index} className={`${teacherModalCss.tModalQItem} ${question.isCorrect ? teacherModalCss.tModalQCorrect : teacherModalCss.tModalQWrong}`}>
                          <div className={teacherModalCss.tModalQHeader}>
                            <span className={teacherModalCss.tModalQBadge}>{question.isCorrect ? '✓' : '✗'}</span>
                            <p className={teacherModalCss.tModalQText}><strong>Q{index + 1}.</strong> {question.questionText}</p>
                          </div>
                          <div className={teacherModalCss.tModalQAnswers}>
                            {!question.isCorrect && question.chosen && <span className={teacherModalCss.tModalQYours}>Student: {question.chosen}</span>}
                            <span className={teacherModalCss.tModalQCorrect2}>Correct: {question.correct}</span>
                          </div>
                          {question.explanation && <p className={teacherModalCss.tModalQExplanation}>{question.explanation}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <p className={teacherModalCss.tModalEmpty}>Practice was completed but no result data is available (may have been done before tracking was enabled).</p>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── SEGMENT: Notifications & Feedback ───────────────────────────────────────

function NotificationsSegment() {
  const [notifs, setNotifs]    = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading]  = useState(true);
  const [tab, setTab]          = useState('notifications');

  useEffect(() => {
    let alive = true;
    Promise.all([
      studentFetch('/student/notifications').then(r => r.json()),
      studentFetch('/student/feedback').then(r => r.json()),
    ]).then(([n, f]) => {
      if (!alive) return;
      setNotifs(n.notifications || []);
      setFeedback(f.feedback || []);
      setLoading(false);
    }).catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  return (
    <div>
      <div className={css.sectionHeader}>
        <h2 className={css.sectionTitle}>Notifications & Feedback</h2>
      </div>

      <div className={css.tabs}>
        <button className={`${css.tab} ${tab === 'notifications' ? css.tabActive : ''}`} onClick={() => setTab('notifications')}>
          Notifications {notifs.length > 0 && `(${notifs.length})`}
        </button>
        <button className={`${css.tab} ${tab === 'feedback' ? css.tabActive : ''}`} onClick={() => setTab('feedback')}>
          Teacher Feedback {feedback.length > 0 && `(${feedback.length})`}
        </button>
      </div>

      <div className={css.card}>
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className={css.notifItem}>
              <div className={css.skel} style={{ width: '100%', height: 40 }} />
            </div>
          ))
        ) : tab === 'notifications' ? (
          notifs.length === 0 ? (
            <div className={css.emptyState}>
              <NotificationsIcon style={{ fontSize: '2rem', color: '#c0ccda' }} />
              <p className={css.emptyTitle}>No notifications</p>
              <p className={css.emptyDesc}>You're all caught up.</p>
            </div>
          ) : (
            notifs.map((n, i) => (
              <div key={i} className={css.notifItem}>
                <div className={css.notifDot} />
                <div>
                  <p className={css.notifTitle}>{n.message || n.title || 'Notification'}</p>
                  <p className={css.notifMeta}>{fmtDate(n.createdAt || n.date)}</p>
                </div>
              </div>
            ))
          )
        ) : (
          feedback.length === 0 ? (
            <div className={css.emptyState}>
              <PersonIcon style={{ fontSize: '2rem', color: '#c0ccda' }} />
              <p className={css.emptyTitle}>No feedback yet</p>
              <p className={css.emptyDesc}>Your teacher's notes will appear here.</p>
            </div>
          ) : (
            feedback.map((f, i) => (
              <div key={i} className={css.notifItem}>
                <div className={css.notifDot} style={{ background: '#0c7c84' }} />
                <div>
                  <p className={css.notifTitle} style={{ marginBottom: 5 }}>{f.note}</p>
                  <p className={css.notifMeta}>
                    From <strong>{f.teacherName}</strong> · {fmtDate(f.createdAt)}
                  </p>
                </div>
              </div>
            ))
          )
        )}
      </div>
    </div>
  );
}

// ─── SEGMENT: Curriculum (view + download only) ──────────────────────────────
// Same card styling/layout as the teacher dashboard's Curriculum tab, minus
// the upload/replace/delete actions — students can only view and download.
function CurriculumSegment() {
  const [subjects, setSubjects] = useState([]);
  const [className, setClassName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewSubject, setViewSubject] = useState(null);
  // Coverage state — keyed by subjectId
  const [coverage, setCoverage] = useState({});
  const [coverageLoading, setCoverageLoading] = useState({});
  const [coverageOpen, setCoverageOpen] = useState({});

  useEffect(() => {
    let alive = true;
    setLoading(true);
    studentFetch('/student/curriculum').then(r => r.json()).then(data => {
      if (!alive) return;
      setSubjects(data.subjects || []);
      setClassName(data.className || '');
      setLoading(false);
    }).catch(() => { if (alive) { setError('Could not load curriculum.'); setLoading(false); } });
    return () => { alive = false; };
  }, []);

  // Auto-load coverage the first time a subject with topics is seen
  useEffect(() => {
    subjects.forEach(sub => {
      if (sub.curriculum?.topics?.length > 0 && !coverage[sub._id] && !coverageLoading[sub._id]) {
        loadCoverage(sub._id);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjects]);

  const loadCoverage = async (subjectId) => {
    setCoverageLoading(prev => ({ ...prev, [subjectId]: true }));
    const res = await studentFetch(`/student/curriculum/coverage/${subjectId}`);
    const data = await res.json().catch(() => ({}));
    if (res.ok) setCoverage(prev => ({ ...prev, [subjectId]: data.coverage }));
    setCoverageLoading(prev => ({ ...prev, [subjectId]: false }));
  };

  return (
    <div>
      <div className={css.sectionHeader}>
        <h2 className={css.sectionTitle}>Curriculum{className ? ` — ${className}` : ''}</h2>
      </div>
      <p style={{ fontSize: '0.82rem', color: '#78909c', margin: '-6px 0 16px' }}>
        Curriculum documents uploaded by your teachers for each of your subjects.
      </p>

      {error && <div style={{ background: '#fdecea', color: '#c62828', border: '1px solid #f5c6cb', borderRadius: 8, padding: '10px 14px', fontSize: '0.85rem', marginBottom: 14 }}>{error}</div>}

      {loading ? (
        <div className={css.curriculumGrid}>
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className={css.curriculumCard}>
              <div className={css.skel} style={{ height: 16, width: '60%', marginBottom: 10 }} />
              <div className={css.skel} style={{ height: 12, width: '90%', marginBottom: 6 }} />
              <div className={css.skel} style={{ height: 12, width: '70%' }} />
            </div>
          ))}
        </div>
      ) : subjects.length === 0 ? (
        <div className={css.emptyState}>
          <MenuBookIcon style={{ fontSize: '2rem', color: '#c0ccda' }} />
          <p className={css.emptyTitle}>No subjects found</p>
          <p className={css.emptyDesc}>Curriculum will appear here once your class and subjects are set up.</p>
        </div>
      ) : (
        <div className={css.curriculumGrid}>
          {subjects.map(sub => {
            const hasCurriculum = !!(sub.curriculum?.key);
            return (
              <div key={sub._id} className={`${css.curriculumCard} ${hasCurriculum ? css.curriculumCardFilled : ''}`}>
                <div className={css.curriculumCardHeader}>
                  <MenuBookIcon fontSize="small" style={{ color: hasCurriculum ? '#1a6e73' : '#90a4ae' }} />
                  <span className={css.curriculumCardName}>{sub.name}</span>
                  {hasCurriculum && <span className={css.curriculumBadge}>Uploaded</span>}
                </div>

                {hasCurriculum ? (
                  <>
                    <div className={css.curriculumCardMeta}>
                      <span>{sub.curriculum.originalName}</span>
                      <span style={{ color: '#90a4ae', fontSize: '0.75rem' }}>
                        {sub.curriculum.uploadedAt ? new Date(sub.curriculum.uploadedAt).toLocaleDateString() : ''}
                      </span>
                    </div>

                    {sub.curriculum.topics?.length > 0 && (
                      <div className={css.curriculumTopics}>
                        {sub.curriculum.topics.slice(0, 6).map((t, i) => (
                          <span key={i} className={css.curriculumTopicChip}>{t}</span>
                        ))}
                        {sub.curriculum.topics.length > 6 && (
                          <span className={css.curriculumTopicMore}>+{sub.curriculum.topics.length - 6} more</span>
                        )}
                      </div>
                    )}

                    {/* Topic coverage — how many topics already have exam questions */}
                    {sub.curriculum.topics?.length > 0 && (() => {
                      const cov = coverage[sub._id];
                      const covLoading = !!coverageLoading[sub._id];
                      const isCovOpen = !!coverageOpen[sub._id];
                      return (
                        <div className={css.coverageSection}>
                          <button
                            className={css.coverageToggleBtn}
                            onClick={() => setCoverageOpen(prev => ({ ...prev, [sub._id]: !isCovOpen }))}
                            type="button"
                          >
                            <span>Topic Coverage</span>
                            {cov && !covLoading && (
                              <span className={css.coverageSummaryBadge}>{cov.coveredCount}/{cov.total}</span>
                            )}
                            <span className={css.coverageChevron}>{isCovOpen ? '▲' : '▼'}</span>
                          </button>
                          <div className={`${css.coverageBody} ${isCovOpen ? css.coverageBodyOpen : ''}`}>
                            {covLoading ? (
                              <div className={css.coverageLoading}>Checking coverage…</div>
                            ) : cov ? (
                              <>
                                <div className={css.coverageBarWrap}>
                                  <div className={css.coverageBar}>
                                    <div
                                      className={css.coverageBarFill}
                                      style={{ width: cov.total ? `${Math.round((cov.coveredCount / cov.total) * 100)}%` : '0%' }}
                                    />
                                  </div>
                                  <span className={css.coverageBarLabel}>
                                    {cov.coveredCount} of {cov.total} topics have exam questions
                                    {cov.total > 0 && ` (${Math.round((cov.coveredCount / cov.total) * 100)}%)`}
                                  </span>
                                </div>
                                <div className={css.coverageTopicsGrid}>
                                  {cov.topics.map((t, i) => (
                                    <span
                                      key={i}
                                      className={`${css.coverageTopicChip} ${t.covered ? css.coverageTopicChipCovered : css.coverageTopicChipUncovered}`}
                                      title={t.covered ? `${t.questionCount} question${t.questionCount !== 1 ? 's' : ''}` : 'No questions yet'}
                                    >
                                      {t.covered ? '✓' : '○'} {t.name}
                                    </span>
                                  ))}
                                </div>
                              </>
                            ) : (
                              <div className={css.coverageLoading}>
                                <button className={css.coverageRefreshBtn} onClick={() => loadCoverage(sub._id)} type="button">
                                  Load coverage data
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}

                    <div className={css.curriculumCardActions}>
                      <button className={css.curriculumActionBtn} onClick={() => setViewSubject(sub)} title="View details">
                        <VisibilityIcon fontSize="small" /> View
                      </button>
                      <a className={css.curriculumActionBtn} href={sub.curriculum.url} download={sub.curriculum.originalName} title="Download">
                        <DownloadIcon fontSize="small" /> Download
                      </a>
                    </div>
                  </>
                ) : (
                  <div className={css.curriculumUploadArea}>
                    <p className={css.curriculumUploadHint}>No curriculum uploaded yet for this subject.</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* View / detail modal */}
      {viewSubject && (
        <div className={css.spModalOverlay} onClick={() => setViewSubject(null)}>
          <div className={`${css.spModalBox} BubblePopIn`} style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
            <div className={css.spModalHeader}>
              <div>
                <h3 className={css.spModalTitle}>{viewSubject.name} — Curriculum</h3>
                <p className={css.spModalSub}>{className}</p>
              </div>
              <button className={css.spModalClose} onClick={() => setViewSubject(null)}>✕</button>
            </div>
            <div style={{ padding: '18px 24px 24px' }}>
              <p style={{ fontSize: '0.82rem', color: '#546e7a', marginBottom: 8 }}>
                <strong>File:</strong> {viewSubject.curriculum?.originalName} &nbsp;·&nbsp;
                Uploaded {viewSubject.curriculum?.uploadedAt ? new Date(viewSubject.curriculum.uploadedAt).toLocaleDateString() : '—'}
              </p>
              {viewSubject.curriculum?.summary && (
                <>
                  <h4 style={{ marginBottom: 6, color: '#263238', fontSize: '0.85rem' }}>Summary</h4>
                  <p style={{ fontSize: '0.83rem', color: '#37474f', lineHeight: 1.6, marginBottom: 16 }}>{viewSubject.curriculum.summary}</p>
                </>
              )}
              {viewSubject.curriculum?.topics?.length > 0 && (
                <>
                  <h4 style={{ marginBottom: 8, color: '#263238', fontSize: '0.85rem' }}>Topics ({viewSubject.curriculum.topics.length})</h4>
                  <div className={css.curriculumTopics} style={{ flexWrap: 'wrap' }}>
                    {viewSubject.curriculum.topics.map((t, i) => (
                      <span key={i} className={css.curriculumTopicChip}>{t}</span>
                    ))}
                  </div>
                </>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                <a className={css.primaryBtn} href={viewSubject.curriculum?.url} download={viewSubject.curriculum?.originalName} style={{ textDecoration: 'none' }}>
                  <DownloadIcon fontSize="small" /> Download File
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── SEGMENT: AI Career Path (read-only — generation is cron-only) ───────────
function CareerPathSegment() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    studentFetch('/student/career-path').then(r => r.json()).then(d => {
      if (!alive) return;
      setData(d);
      setLoading(false);
    }).catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  return (
    <div>
      <div className={css.sectionHeader}>
        <h2 className={css.sectionTitle}>Your Career Path</h2>
      </div>

      <div className={css.card}>
        {loading ? (
          <div className={css.skel} style={{ width: '100%', height: 120, borderRadius: 10 }} />
        ) : (
          <CareerPathSummaryCard
            enabled={data?.enabled}
            status={data?.status}
            eligibility={data?.eligibility}
            snapshot={data?.snapshot}
            lastComputedAt={data?.lastComputedAt}
          />
        )}
      </div>
    </div>
  );
}

// ─── SEGMENT: Calendar (read-only, audience-filtered, Premium-gated) ─────────

function calEventTypeBadgeClass(type) {
  return {
    exam: css.calBadgeExam, holiday: css.calBadgeHoliday, meeting: css.calBadgeMeeting,
    deadline: css.calBadgeDeadline, activity: css.calBadgeActivity,
  }[type] || css.calBadgeOther;
}
function fmtCalDateHeading(d) {
  return new Date(d).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
}
function fmtCalTime(d) {
  return new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function CalendarSegment({ profile }) {
  const managementEnabled = profile?.subscription?.plan === 'premium' && profile?.subscription?.schoolManagementEnabled !== false;
  const [monthDate, setMonthDate] = useState(() => { const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); return d; });
  const [events, setEvents]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    if (!managementEnabled) { setLoading(false); return; }
    setLoading(true);
    const from = monthDate.toISOString().slice(0, 10);
    const to = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0).toISOString().slice(0, 10);
    studentFetch(`/student/calendar?from=${from}&to=${to}`)
      .then(r => r.json())
      .then(data => { setEvents(data.events || []); setError(data.error || ''); })
      .catch(() => setError('Connection error.'))
      .finally(() => setLoading(false));
  }, [monthDate, managementEnabled]);

  if (!managementEnabled) {
    return (
      <div>
        <div className={css.sectionHeader}>
          <h2 className={css.sectionTitle}>Calendar</h2>
        </div>
        <div className={css.card}>
          <div className={css.premiumLockBanner}>
            🔒 Calendar &amp; Events is a <strong>Premium</strong> feature. Ask your school admin to upgrade the plan to unlock it.
          </div>
        </div>
      </div>
    );
  }

  const grouped = {};
  events.forEach(ev => {
    const key = new Date(ev.startDate).toISOString().slice(0, 10);
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(ev);
  });
  const sortedKeys = Object.keys(grouped).sort();

  return (
    <div>
      <div className={css.sectionHeader}>
        <h2 className={css.sectionTitle}>Calendar</h2>
      </div>
      {error && <div className={css.inlineError} style={{ marginBottom: 16 }}>{error}</div>}
      <div className={css.card}>
        <div className={css.calHeaderRow}>
          <button className={css.calNavBtn} onClick={() => setMonthDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1))}>‹</button>
          <span className={css.calMonthLabel}>{monthDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</span>
          <button className={css.calNavBtn} onClick={() => setMonthDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1))}>›</button>
        </div>

        {loading ? (
          <div className={css.skel} style={{ width: '100%', height: 80, borderRadius: 10 }} />
        ) : sortedKeys.length === 0 ? (
          <div className={css.emptyState}>
            <p>No events this month.</p>
          </div>
        ) : (
          <div className={css.calAgenda}>
            {sortedKeys.map(dateKey => (
              <div key={dateKey} className={css.calDayGroup}>
                <div className={css.calDayHeading}>{fmtCalDateHeading(dateKey)}</div>
                {grouped[dateKey].map(ev => (
                  <div key={ev._id} className={css.calEventCard}>
                    <span className={`${css.calBadge} ${calEventTypeBadgeClass(ev.type)}`}>{ev.type}</span>
                    <div>
                      <div className={css.calEventTitle}>{ev.title}</div>
                      <div className={css.calEventMeta}>
                        {!ev.allDay && `${fmtCalTime(ev.startDate)}${ev.endDate ? ` – ${fmtCalTime(ev.endDate)}` : ''} · `}
                        {ev.audience === 'class'
                          ? ((ev.classIds || []).map(c => c.name).join(', ') || 'Class')
                          : ev.audience.charAt(0).toUpperCase() + ev.audience.slice(1)}
                        {ev.location ? ` · ${ev.location}` : ''}
                      </div>
                      {ev.description && <div className={css.calEventDesc}>{ev.description}</div>}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── SEGMENT: Attendance ──────────────────────────────────────────────────────

function AttendanceSegment({ profile }) {
  const managementEnabled = profile?.subscription?.plan === 'premium' && profile?.subscription?.schoolManagementEnabled !== false;
  const [stats, setStats]   = useState({ present: 0, total: 0, rate: null });
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    if (!managementEnabled) { setLoading(false); return; }
    setLoading(true);
    studentFetch('/student/attendance')
      .then(r => r.json())
      .then(data => {
        setStats(data.stats || { present: 0, total: 0, rate: null });
        setRecords(data.records || []);
        setError(data.error || '');
      })
      .catch(() => setError('Connection error.'))
      .finally(() => setLoading(false));
  }, [managementEnabled]);

  if (!managementEnabled) {
    return (
      <div>
        <div className={css.sectionHeader}>
          <h2 className={css.sectionTitle}>Attendance</h2>
        </div>
        <div className={css.card}>
          <div className={css.premiumLockBanner}>
            🔒 Attendance is a <strong>Premium</strong> feature. Ask your school admin to upgrade the plan to unlock it.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={css.sectionHeader}>
        <h2 className={css.sectionTitle}>Attendance</h2>
      </div>
      {error && <div className={css.inlineError} style={{ marginBottom: 16 }}>{error}</div>}
      <div className={css.card}>
        {loading ? (
          <div className={css.skel} style={{ width: '100%', height: 80, borderRadius: 10 }} />
        ) : (
          <>
            <div className={css.statCard} style={{ marginBottom: 20, maxWidth: 220 }}>
              <span className={css.statLabel}>Attendance Rate</span>
              <span className={css.statValue} style={{ color: stats.rate == null ? undefined : stats.rate < 75 ? '#c62828' : '#10b981' }}>
                {stats.rate == null ? '—' : `${stats.rate}%`}
              </span>
              <span style={{ fontSize: '0.78rem', color: '#78909c' }}>{stats.present}/{stats.total} periods present</span>
            </div>
            {records.length === 0 ? (
              <div className={css.emptyState}>
                <p>No attendance records yet.</p>
              </div>
            ) : (
              <div className={css.tableWrap}>
              <table className={css.table}>
                <thead>
                  <tr><th>Date</th><th>Subject</th><th>Period</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {records.map((r, i) => (
                    <tr key={i}>
                      <td>{r.date}</td>
                      <td>{r.subjectName || '—'}</td>
                      <td>{r.periodLabel || '—'}</td>
                      <td style={{ textTransform: 'capitalize', fontWeight: 600, color: r.status === 'present' ? '#2e7d32' : r.status === 'absent' ? '#c62828' : r.status === 'late' ? '#e65100' : '#546e7a' }}>
                        {r.status}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── SEGMENT: Profile & Settings ─────────────────────────────────────────────

function ProfileSegment() {
  const [profile, setProfile]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState('');
  const [avatarSaving, setAvatarSaving] = useState(false);
  const [avatarMsg, setAvatarMsg] = useState(null);
  const [pwForm, setPwForm]     = useState({ current: '', next: '', confirm: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwMsg, setPwMsg]       = useState(null);
  const avatarUrlRef = useRef('');

  useEffect(() => {
    studentFetch('/student/profile').then(r => r.json()).then(d => {
      setProfile(d.profile || null);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => () => {
    if (avatarUrlRef.current) URL.revokeObjectURL(avatarUrlRef.current);
  }, []);

  const handleAvatarChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const validation = validateImageFile(file, 2);
    if (!validation.valid) {
      setAvatarMsg({ error: validation.error });
      return;
    }
    setAvatarMsg(null);
    let finalFile = file;
    if (file.type === 'image/webp' || file.type === 'image/gif') {
      try {
        finalFile = await convertToJpeg(file);
      } catch {
        setAvatarMsg({ error: 'Image conversion failed. Try JPEG or PNG format.' });
        return;
      }
    }
    if (finalFile.size > 2 * 1024 * 1024) {
      try {
        const compressed = await compressImageIfNeeded(finalFile, 2);
        finalFile = compressed.file;
      } catch {
        setAvatarMsg({ error: 'Image compression failed. Try a smaller passport photo.' });
        return;
      }
    }
    if (avatarUrlRef.current) URL.revokeObjectURL(avatarUrlRef.current);
    const objectUrl = URL.createObjectURL(finalFile);
    avatarUrlRef.current = objectUrl;
    setAvatarFile(finalFile);
    setAvatarPreview(objectUrl);
  };

  const handleAvatarSave = async () => {
    if (!avatarFile) {
      setAvatarMsg({ error: 'Choose an image first.' });
      return;
    }
    setAvatarSaving(true);
    setAvatarMsg(null);
    try {
      const payload = new FormData();
      payload.append('file', avatarFile);
      const res = await studentFetch('/student/profile-image', {
        method: 'POST',
        body: payload,
      });
      const data = await res.json();
      if (res.ok) {
        const nextProfileImg = data.profileImg || avatarPreview;
        setProfile(p => p ? { ...p, profileImg: nextProfileImg } : p);
        setAvatarFile(null);
        setAvatarPreview('');
        if (avatarUrlRef.current) URL.revokeObjectURL(avatarUrlRef.current);
        avatarUrlRef.current = '';
        setAvatarMsg({ success: 'Passport photo updated successfully.' });
      } else {
        setAvatarMsg({ error: data.error || 'Failed to update passport photo.' });
      }
    } catch {
      setAvatarMsg({ error: 'Network error. Please try again.' });
    } finally {
      setAvatarSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwMsg(null);
    if (pwForm.next !== pwForm.confirm) { setPwMsg({ error: 'Passwords do not match' }); return; }
    if (pwForm.next.length < 8)        { setPwMsg({ error: 'Password must be at least 8 characters' }); return; }
    setPwLoading(true);
    try {
      const res = await studentFetch('/student/change-password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword: pwForm.current, newPassword: pwForm.next, confirmPassword: pwForm.confirm }),
      });
      const data = await res.json();
      if (res.ok) {
        setPwMsg({ success: 'Password changed successfully!' });
        setPwForm({ current: '', next: '', confirm: '' });
      } else {
        setPwMsg({ error: data.error || 'Failed to change password' });
      }
    } catch {
      setPwMsg({ error: 'Network error. Please try again.' });
    } finally {
      setPwLoading(false);
    }
  };

  const fields = profile ? [
    { label: 'Full Name',   value: `${profile.firstName} ${profile.lastName}` },
    { label: 'Email',       value: profile.email },
    { label: 'Student ID',  value: profile.studentId || '—' },
    { label: 'Class Arm',   value: profile.classArm  || '—' },
    { label: 'School',      value: profile.schoolName || '—' },
    { label: 'Date of Birth', value: profile.dob ? new Date(profile.dob).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—' },
    { label: 'Sex',         value: profile.sex || '—' },
    { label: 'Mobile Number', value: profile.mobile || '—' },
    { label: 'Street / City', value: profile.streetCity || '—' },
    { label: 'State / Region', value: profile.stateRegion || '—' },
    { label: 'Country',     value: profile.country || '—' },
    { label: 'Subjects',    value: (profile.subjects || []).join(', ') || '—' },
    { label: 'Term',        value: profile.term    || '—' },
    { label: 'Session',     value: profile.session || '—' },
    { label: 'Other Notes', value: profile.demographicNotes || '—' },
  ] : [];

  const avatarSrc = avatarPreview || profile?.profileImg || '/uploads/avatar.png';

  return (
    <div>
      <div className={css.sectionHeader}>
        <h2 className={css.sectionTitle}>Profile & Settings</h2>
      </div>

      <div className={css.profileSettingsGrid}>
        {/* Profile info */}
        <div className={css.card}>
          <h3 className={css.profileCardTitle}>My Profile</h3>
          {loading ? (
            Array.from({length:10}).map((_,i) => (
              <div key={i} className={css.profileField}>
                <div className={css.skel} style={{ height: 10, width: '40%', marginBottom: 6 }} />
                <div className={css.skel} style={{ height: 14, width: '70%' }} />
              </div>
            ))
          ) : fields.map(({ label, value }) => (
            <div key={label} className={css.profileField}>
              <span className={css.profileFieldLabel}>{label}</span>
              <span className={css.profileFieldValue}>{value}</span>
            </div>
          ))}
        </div>

        <div className={css.profileSettingsSide}>
          <div className={css.card}>
            <h3 className={css.profileCardTitle}>Passport Photo</h3>
            <div className={css.studentPassportBox}>
              <div className={css.studentPassportPreview}>
                {avatarSrc !== '/uploads/avatar.png'
                  ? <img src={avatarSrc} alt="Student passport preview" />
                  : <span>{initials(profile?.firstName, profile?.lastName)}</span>}
              </div>
              <div className={css.studentPassportMeta}>
                <div className={css.studentPassportName}>{profile?.firstName} {profile?.lastName}</div>
                <div className={css.studentPassportHint}>Upload a passport-style image. Only you can change this image.</div>
                {avatarMsg?.error && <div className={css.inlineError} style={{ marginTop: 12 }}>{avatarMsg.error}</div>}
                {avatarMsg?.success && <div className={css.inlineSuccess} style={{ marginTop: 12, marginBottom: 0 }}>{avatarMsg.success}</div>}
                <div className={css.studentPassportActions}>
                  <input className={css.studentPassportInput} type="file" accept="image/*" onChange={handleAvatarChange} />
                  <button type="button" className={css.primaryBtn} onClick={handleAvatarSave} disabled={avatarSaving || !avatarFile}>
                    {avatarSaving ? 'Saving…' : 'Save Passport Photo'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className={css.card}>
            <h3 className={css.profileCardTitle}>Change Password</h3>
            <form onSubmit={handleChangePassword} noValidate>
              {pwMsg?.error && (
                <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: '0.85rem' }}>
                  {pwMsg.error}
                </div>
              )}
              {pwMsg?.success && (
                <div style={{ background: '#d1fae5', color: '#065f46', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: '0.85rem' }}>
                  {pwMsg.success}
                </div>
              )}
              <div className={css.formGroup}>
                <label className={css.formLabel}>Current Password</label>
                <SchoolPasswordInput
                  className={css.formInput}
                  placeholder="Your current password"
                  value={pwForm.current}
                  onChange={e => setPwForm(f => ({ ...f, current: e.target.value }))}
                  required
                />
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>New Password</label>
                <SchoolPasswordInput
                  className={css.formInput}
                  placeholder="Min. 8 characters"
                  value={pwForm.next}
                  onChange={e => setPwForm(f => ({ ...f, next: e.target.value }))}
                  required
                />
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Confirm New Password</label>
                <SchoolPasswordInput
                  className={css.formInput}
                  placeholder="Repeat new password"
                  value={pwForm.confirm}
                  onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))}
                  required
                />
              </div>
              <button type="submit" className={css.primaryBtn} disabled={pwLoading}>
                {pwLoading ? 'Saving…' : 'Change Password'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

function StudentForceChangePasswordModal({ onDone }) {
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const set = key => event => setForm(current => ({ ...current, [key]: event.target.value }));

  const handleSubmit = async event => {
    event.preventDefault();
    setError('');
    if (!form.currentPassword || !form.newPassword || !form.confirmPassword) {
      setError('Enter your current password, a new password, and the confirmation.');
      return;
    }
    if (form.newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (form.newPassword !== form.confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const res = await studentFetch('/student/change-password', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || data.message || 'Failed to change password.');
        return;
      }
      localStorage.removeItem('school-student-must-change-pwd');
      setSuccess(true);
      window.setTimeout(onDone, 1000);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={css.spModalOverlay} style={{ zIndex: 9999 }} role="presentation">
      <div className={`${css.spModalBox} BubblePopIn`} style={{ maxWidth: 460 }} role="dialog" aria-modal="true" aria-labelledby="student-password-title">
        <div className={css.spModalHeader}>
          <div>
            <h2 className={css.spModalTitle} id="student-password-title">
              <LockIcon style={{ fontSize: '1.05rem', marginRight: 8, color: '#5fbec5' }} />
              Set your password
            </h2>
            <p className={css.spModalSub}>Change your temporary password before continuing to your student portal.</p>
          </div>
        </div>

        <div style={{ padding: '22px 24px 24px' }}>
          {success ? (
            <p style={{ margin: 0, color: '#3e8f93', fontWeight: 700, textAlign: 'center' }} role="status">
              Password changed successfully. Loading your dashboard…
            </p>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              {error && (
                <div style={{ color: '#b91c1c', background: '#fff0f0', borderRadius: 8, padding: '10px 12px', marginBottom: 16, fontSize: '0.85rem' }} role="alert">
                  {error}
                </div>
              )}
              <div className={css.formGroup}>
                <label className={css.formLabel} htmlFor="student-current-password">Current password</label>
                <SchoolPasswordInput
                  id="student-current-password"
                  className={css.formInput}
                  placeholder="Temporary password"
                  autoComplete="current-password"
                  value={form.currentPassword}
                  onChange={set('currentPassword')}
                  autoFocus
                />
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel} htmlFor="student-new-password">New password</label>
                <SchoolPasswordInput
                  id="student-new-password"
                  className={css.formInput}
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                  value={form.newPassword}
                  onChange={set('newPassword')}
                />
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel} htmlFor="student-confirm-password">Confirm new password</label>
                <SchoolPasswordInput
                  id="student-confirm-password"
                  className={css.formInput}
                  placeholder="Repeat your new password"
                  autoComplete="new-password"
                  value={form.confirmPassword}
                  onChange={set('confirmPassword')}
                />
              </div>
              <button type="submit" className={css.primaryBtn} disabled={loading} style={{ width: '100%', justifyContent: 'center' }}>
                {loading ? 'Saving…' : 'Set Password & Continue'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Root Dashboard ───────────────────────────────────────────────────────────

export default function StudentDashboard() {
  const [activeTab, setActiveTab]       = useState(() => {
    const requested = new URLSearchParams(window.location.search).get('tab');
    return NAV.some(item => item.key === requested) ? requested : 'overview';
  });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [profile, setProfile]           = useState(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [mustChangePwd, setMustChangePwd] = useState(() => localStorage.getItem('school-student-must-change-pwd') === '1');
  const [jumpToExamId, setJumpToExamId] = useState(null); // schoolExamId to auto-open in Results
  const navigate                        = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('school-token');
    const actor = localStorage.getItem('school-actor');
    if (!token || actor !== 'student') {
      navigate('/school', { replace: true });
      return;
    }
    studentFetch('/student/profile').then(async response => ({
      ok: response.ok,
      data: await response.json(),
    })).then(({ ok, data }) => {
      if (!ok) {
        setProfileLoaded(true);
        return;
      }
      setProfile(data.profile || null);
      if (data.profile?.mustChangePassword) {
        localStorage.setItem('school-student-must-change-pwd', '1');
        setMustChangePwd(true);
      } else if (data.profile && data.profile.mustChangePassword === false) {
        localStorage.removeItem('school-student-must-change-pwd');
        setMustChangePwd(false);
      }
      setProfileLoaded(true);
    }).catch(() => setProfileLoaded(true));
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('school-token');
    localStorage.removeItem('school-actor');
    localStorage.removeItem('school-id');
    localStorage.removeItem('token'); // clear bridge token
    localStorage.removeItem('school-student-must-change-pwd');
    navigate('/school', { replace: true });
  };

  const handleInsight = (schoolExamId, defaultTab = 'grading') => {
    setJumpToExamId({ id: schoolExamId, tab: defaultTab });
    setActiveTab('results');
  };

  const segmentMap = {
    overview:      <OverviewSegment profile={profile} onGoToExams={() => setActiveTab('exams')} onInsight={handleInsight} />,
    exams:         <ExamsSegment onInsight={handleInsight} />,
    results:       <ResultsSegment jumpTo={jumpToExamId} onJumpCleared={() => setJumpToExamId(null)} />,
    curriculum:    <CurriculumSegment />,
    calendar:      <CalendarSegment profile={profile} />,
    timetable:     <TimetableGrid mode="single" fetchFn={studentFetch} basePath="/student" term={profile?.term} session={profile?.session} managementEnabled={profile?.subscription?.plan === 'premium' && profile?.subscription?.schoolManagementEnabled !== false} />,
    attendance:    <AttendanceSegment profile={profile} />,
    messages: (
      <MessagingSegment
        fetchFn={studentFetch}
        basePath="/student"
        actorType="student"
        currentActorId={profile?._id}
        managementEnabled={profile?.subscription?.plan === 'premium' && profile?.subscription?.schoolManagementEnabled !== false}
      />
    ),
    'career-path': <CareerPathSegment />,
    notifications: <NotificationsSegment />,
    profile:       <ProfileSegment />,
  };

  const activeNav = NAV.find(n => n.key === activeTab) || NAV[0];
  const studentPortalReady = profileLoaded && !mustChangePwd;
  const handleForcedPasswordDone = () => {
    setProfile(current => current ? { ...current, mustChangePassword: false } : current);
    setProfileLoaded(true);
    setMustChangePwd(false);
  };

  return (
    <div className={css.shell}>
      {mustChangePwd && <StudentForceChangePasswordModal onDone={handleForcedPasswordDone} />}
      {mobileNavOpen && <div className={css.navOverlay} onClick={() => setMobileNavOpen(false)} />}
      {/* ── Sidebar ── */}
      <aside className={`${css.sidebar} ${mobileNavOpen ? css.sidebarOpen : ''}`}>
        <div className={css.sidebarLogo}>
          <img src={profile?.schoolLogo || '/favicon.ico'} alt={profile?.schoolName || 'Bubble Ai Schools'} className={css.sidebarLogoImg} />
          <span className={css.sidebarLogoText}>{profile?.schoolName || 'Bubble Ai Schools'}</span>
        </div>

        <nav>
          {NAV.map(({ key, label, Icon }) => (
            <div
              key={key}
              className={`${css.navItem} ${activeTab === key ? css.navItemActive : ''}`}
              onClick={() => { setActiveTab(key); setMobileNavOpen(false); }}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && setActiveTab(key)}
            >
              <Icon className={css.navIcon} />
              {label}
            </div>
          ))}
        </nav>

        <div className={css.sidebarSpacer} />

        <div className={css.sidebarFooter}>
          <button className={css.logoutBtn} onClick={handleLogout}>
            <LogoutIcon style={{ fontSize: '1.1rem' }} />
            Log Out
          </button>
        </div>
      </aside>

      {/* ── Main ── */}
      <main className={css.main}>
        {/* Topbar */}
        <header className={css.topbar}>
          <div className={css.topbarLeft}>
            <button className={css.hamburgerBtn} onClick={() => setMobileNavOpen(true)} aria-label="Open menu">
              <MenuIcon fontSize="small" />
            </button>
            <h1 className={css.topbarTitle}>{activeNav.label}</h1>
          </div>
          <div className={css.topbarRight}>
            {profile && (
              <div className={css.studentBadge}>
                <div className={css.studentAvatar}>
                  {initials(profile.firstName, profile.lastName)}
                </div>
                {profile.firstName} {profile.lastName}
              </div>
            )}
            <SchoolNotificationCenter inline />
          </div>
        </header>

        {/* Page body */}
        {studentPortalReady && (
          <div className={css.pageBody}>
            {activeTab === 'overview' && <SchoolPWAInstallBanner />}
            {segmentMap[activeTab] || segmentMap.overview}
            <SchoolPoweredFooter role="student" />
          </div>
        )}
      </main>
    </div>
  );
}
